// Map overlays for the SeaState console — ported faithfully from the v1 single-file
// demo (index.html): NDBC buoys, NHC tropical systems, the maritime-jurisdiction /
// lease / closure layer control, and the EEZ point-identify used by the console.
//
// This is the ONE module allowed to touch the raw MapLibre map (see map.ts `raw()`).
// It adds sources/layers directly and appends its own DOM (badge + control) to the
// map container. Every upstream fetch is graceful: a dead source logs `console.warn`
// and degrades the UI (a toggled overlay simply unchecks itself), never throwing into
// a blank page — the v1 `Promise.allSettled` / try-catch discipline.
import maplibregl from "maplibre-gl";
import type { GeoJSONSource, LayerSpecification, Map as MlMap } from "maplibre-gl";
// Expression casts: MapLibre's paint expressions are a large tuple union that plain
// array literals don't structurally match. This is MapLibre's own type for the DSL,
// used only to assert the hand-written expressions below — never `any`.
import type { ExpressionSpecification } from "@maplibre/maplibre-gl-style-spec";
import { registerMapIcons, refreshMapIcons } from "../ui/mapicons.js";
import { refreshArrowColors } from "./arrowfield.js";
import { mergeCurrentStorm } from "../data/stormlist.js";
import { infoButton } from "../help/popover.js";
import { loadAllStations, buoySource } from "../forecast/buoys/registry.js";
import { drifterAgeHours, type Drifter } from "../forecast/drifters.js";
import type { HelpKey } from "../help/types.js";
import { convTemp, fmtLimitLen, getUnitSystem, tempUnit } from "../ui/units.js";

/** A clicked buoy — the point the console should load a forecast for. `source`
 *  keys which network it came from (ndbc / aodn / …) so obs + labels route right. */
export interface BuoyPick {
  source: string;
  id: string;
  name: string;
  lat: number;
  lon: number;
}

/** Which maritime jurisdiction a point sits in, and whether it is contested. */
export interface Jurisdiction {
  name: string;
  territory: string | null;
  sovereign: string | null;
  disputed: boolean;
  polType: string;
}

// ---------- shared endpoints (identical to v1) ----------
const MR = "https://geo.vliz.be/geoserver/MarineRegions";
const NHC =
  "https://mapservices.weather.noaa.gov/tropical/rest/services/tropical/NHC_tropical_weather/MapServer";

// ---------- palette ----------
// Read CSS custom properties at draw time so the map follows the light/dark theme,
// with hard fallbacks for when the property is unset (e.g. layer built before CSS).
const CSS_FALLBACK: Record<string, string> = {
  "--warn": "#b9791a",
  "--crit": "#b23b3b",
  "--good": "#2e8b57",
  "--m1": "#2f6f9f",
  "--navy": "#16232e",
  "--muted": "#8a969b",
  "--accent": "#0f6b74",
  "--paper": "#f3f4f2",
};
function cssVar(name: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || CSS_FALLBACK[name] || "#000000";
}

// ---------- small typed helpers ----------
function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

/** Read a property as a display string (missing/null → ""), avoiding `any`. */
function str(p: Record<string, unknown>, key: string): string {
  const v = p[key];
  if (typeof v === "string") return v;
  if (v == null) return "";
  return String(v);
}

/** Read a property as a finite number (anything else → null), avoiding `any`. */
function num(p: Record<string, unknown>, key: string): number | null {
  const v = p[key];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

async function getJSON(url: string): Promise<unknown> {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`${url} → HTTP ${r.status}`);
  return r.json();
}

/** Narrow an unknown JSON payload to a GeoJSON FeatureCollection, or null. */
function asFeatureCollection(v: unknown): GeoJSON.FeatureCollection | null {
  if (isRecord(v) && v["type"] === "FeatureCollection" && Array.isArray(v["features"])) {
    return v as unknown as GeoJSON.FeatureCollection;
  }
  return null;
}

/** Point geometry coordinates as a MapLibre-friendly tuple, or null. */
function pointCoords(g: GeoJSON.Geometry): [number, number] | null {
  if (g.type !== "Point") return null;
  const [lon, lat] = g.coordinates;
  return [lon, lat];
}

/** Overlays and tropical layers slot BENEATH the buoys layer when it exists. */
function beforeBuoys(map: MlMap): string | undefined {
  return map.getLayer("buoys") ? "buoys" : undefined;
}

// ===================================================================================
//  NDBC buoy layer (via the same-origin /ndbc relay — NDBC sends no CORS headers)
// ===================================================================================
export async function setupBuoys(
  map: MlMap,
  onPick: (b: BuoyPick) => void,
): Promise<void> {
  try {
    // Every network's stations, merged (NDBC + AODN + …). Each feature carries its
    // source so a click routes obs/labels to the right adapter.
    const stations = await loadAllStations();
    const features: GeoJSON.Feature<
      GeoJSON.Point,
      { source: string; id: string; name: string }
    >[] = stations.map((st) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: [st.lon, st.lat] },
      properties: { source: st.source, id: st.id, name: st.name },
    }));

    map.addSource("buoys", {
      type: "geojson",
      data: { type: "FeatureCollection", features },
    });
    map.addLayer({
      id: "buoys",
      type: "circle",
      source: "buoys",
      // Off by default — the layer exists (so its click→forecast handler is wired)
      // but stays hidden until toggled on in the layer control.
      layout: { visibility: "none" },
      paint: {
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 3, 3, 8, 6] as ExpressionSpecification,
        // v1 used --buoy (amber); our robust palette carries that amber as --warn.
        "circle-color": cssVar("--warn"),
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 1,
      },
    });

    const pop = new maplibregl.Popup({ closeButton: false, closeOnClick: false });
    map.on("mouseenter", "buoys", (e) => {
      map.getCanvas().style.cursor = "pointer";
      const f = e.features?.[0];
      if (!f) return;
      const at = pointCoords(f.geometry);
      if (!at) return;
      const p = f.properties ?? {};
      const label =
        buoySource(str(p, "source"))?.pickLabel({ id: str(p, "id"), name: str(p, "name") }) ??
        str(p, "name");
      pop.setLngLat(at).setText(label).addTo(map);
    });
    map.on("mouseleave", "buoys", () => {
      map.getCanvas().style.cursor = "";
      pop.remove();
    });

    // Buoy click → report the buoy so the console can load its forecast. See the
    // module report for exactly how this interacts with map.ts's base click handler.
    map.on("click", "buoys", (e) => {
      const f = e.features?.[0];
      if (!f) return;
      const at = pointCoords(f.geometry);
      if (!at) return;
      const p = f.properties ?? {};
      onPick({
        source: str(p, "source"),
        id: str(p, "id"),
        name: str(p, "name"),
        lat: at[1],
        lon: at[0],
      });
    });
  } catch (err) {
    console.warn("Buoy layer unavailable:", err);
  }
}

// ===================================================================================
//  NHC tropical layer: development areas, forecast cones, tracks, points + badge
// ===================================================================================
interface ServiceLayer {
  id: number;
  name: string;
}
function asServiceLayers(v: unknown): ServiceLayer[] {
  if (!isRecord(v) || !Array.isArray(v["layers"])) return [];
  const out: ServiceLayer[] = [];
  for (const l of v["layers"]) {
    if (isRecord(l) && typeof l["id"] === "number" && typeof l["name"] === "string") {
      out.push({ id: l["id"], name: l["name"] });
    }
  }
  return out;
}

// Tropical polygons kept for point-in-area lookup by the console (the primer /
// briefing), populated when setupTropical loads. Dev = NHC 7-day outlook areas;
// storms = active systems with their forecast cone(s) plus the current-advisory
// facts off the tau-0 forecast point — the storm list's card data.
let tropDevFeatures: GeoJSON.Feature[] = [];
// Current location of each outlook area (the X on NHC's own graphic), keyed by
// the outlook feature's objectid. The 7-day polygon is where a system MAY
// develop — often well downstream of where the disturbance is now.
let tropDevPoints = new Map<number, { lat: number; lon: number }>();
interface TropStorm {
  name: string;
  cones: GeoJSON.Feature[];
  lat: number | null;
  lon: number | null;
  type: string; // NHC stormtype code: TD / TS / HU / STS / …
  ss: number | null; // Saffir-Simpson number (0 below hurricane strength)
  maxwind: number | null; // kt
  tcdir: number | null; // deg true (9999 = missing, per NHC)
  tcspd: number | null; // kt (9999 = missing)
  basin: string; // AL / EP / CP
  /** The advisory package the MAP geometry came from — "700 PM CDT Tue Sep 01
   *  2026" and "6A". Printed, because on 2026-09-02 this layer was 21 hours
   *  behind and an operator reported the console as wrong. It was not wrong; it
   *  was faithfully drawing a stale upstream, and nothing on screen said so. */
  advDate: string;
  advNum: string;
  /** The service slot ("AT5"), kept so a refresh can re-join the live summary
   *  without re-querying the MapServer. */
  slot: string;
  /** When CurrentStorms.json last revised this storm's classification, if it
   *  carried it. Null means the live summary had nothing for this slot and the
   *  type below is the advisory's. */
  liveAt: string | null;
}
let tropStorms: TropStorm[] = [];

/** One storm as NHC's current-storm summary describes it: classification and
 *  intensity as of minutes ago, rather than as of the last full advisory.
 *  Keyed by binNumber ("AT5"), which is exactly our slot. */
interface CurrentStorm {
  name: string;
  classification: string;
  intensityKt: number | null;
  lastUpdate: string;
}
let currentStorms = new Map<string, CurrentStorm>();

/**
 * NHC's CurrentStorms.json, through our relay (the file sends no CORS header).
 *
 * WHY A SECOND SOURCE. The MapServer publishes on the full advisory package and
 * is the only place cone and track geometry exists, so it stays authoritative
 * for those. It is not authoritative for how strong a storm is NOW: measured on
 * 2026-09-02 it still called Edouard a tropical storm at 40 kt while this file,
 * updated that morning, had him a depression at 20. Geometry from there,
 * classification from here.
 *
 * A failure is not fatal — the advisory's own type is the fallback, which is
 * what the console showed before this existed.
 */
async function loadCurrentStorms(): Promise<void> {
  try {
    const raw = await getJSON("/nhc/CurrentStorms.json");
    const list = isRecord(raw) ? raw["activeStorms"] : null;
    const next = new Map<string, CurrentStorm>();
    if (Array.isArray(list)) {
      for (const item of list) {
        if (!isRecord(item)) continue;
        const bin = str(item, "binNumber").toUpperCase();
        if (bin === "") continue;
        // intensity arrives as a string of knots ("20"); Number("") is 0, which
        // would read as a real calm rather than a missing value.
        const rawInt = str(item, "intensity").trim();
        const kt = rawInt === "" ? null : Number(rawInt);
        next.set(bin, {
          name: str(item, "name"),
          classification: str(item, "classification").toUpperCase(),
          intensityKt: kt !== null && Number.isFinite(kt) ? kt : null,
          lastUpdate: str(item, "lastUpdate"),
        });
      }
    }
    currentStorms = next;
  } catch (err) {
    console.warn("NHC current-storm summary unavailable:", err);
    currentStorms = new Map();
  }
}

// Storms outside NHC's basins (West Pacific, Indian Ocean, Southern Hemisphere),
// from the Esri Living Atlas "Active Hurricanes" live feed, which aggregates
// NHC + JTWC warnings. Listed as cards only — no cones/tracks on the map.
interface IntlStorm {
  name: string;
  type: string; // spelled out: "Typhoon", "Tropical Depression", …
  ss: number | null;
  maxwind: number | null; // kt (feed INTENSITY)
  lat: number;
  lon: number;
  basin: string; // WP / IO / SH / …
  /** Esri's STORMID ("wp292026") cut to basin + number ("wp29"): the join to
   *  JTWC's own warning overlay, whose file id is "wp2926". */
  key: string;
}
let intlStorms: IntlStorm[] = [];

// ===================================================================================
//  JTWC warnings — the gale-force swath for the basins NHC does not cover
// ===================================================================================
//
// The Joint Typhoon Warning Center issues no cone. Its warning graphic carries
// an "area of potential gale force winds" instead — the union of the forecast
// 34-knot wind radii along the track — and its Google Earth overlay carries the
// same shape as a polygon named "34 knot Danger Swath". Les Bender asked for it
// (2026-10-20, a TDI proposal off Timor-Leste in hand) as "a substitute or
// mirror for the warning cone". server/jtwc.go fetches and parses the overlays
// (metoc.navy.mil sends no CORS header); this is the drawing half.
//
// IT IS NOT A CONE, and the card and help say so: where gales are forecast IF
// the track verifies, with no track-error envelope around it. The Esri feed's
// own "danger area" for the same storm started ten degrees north of JTWC's
// swath, which is why the source of record is JTWC's file, not the aggregate.
interface JtwcStorm {
  id: string;
  key: string; // basin + number, lower case ("wp29")
  basin: string; // WP / IO / SH
  number: number;
  name: string; // JTWC's upper case: "CHAMPI"
  title: string; // "TYPHOON 29W (CHAMPI)"
  kind: string; // "TYPHOON" / "TROPICAL STORM" / "TROPICAL CYCLONE" …
  warning: number;
  issued: string; // "221800Z"
  lat: number;
  lon: number;
  windKt: number | null;
  moveDeg: number | null;
  moveKt: number | null;
  hasSwath: boolean;
}
let jtwcStorms: JtwcStorm[] = [];
let jtwcFeatures: GeoJSON.Feature[] = [];
let jtwcMap: MlMap | null = null;

const JTWC_SRC = "jtwc";
const JTWC_HATCH = "jtwc-hatch";

/** "CHAMPI" → "Champi", "TROPICAL STORM" → "Tropical Storm", "29W (CHAMPI)" →
 *  "29W (Champi)": a letter is capitalised after anything that is not a letter. */
function titleCase(s: string): string {
  return s.toLowerCase().replace(/(^|[^a-z])([a-z])/g, (_m, pre: string, ch: string) => pre + ch.toUpperCase());
}

async function loadJTWC(): Promise<boolean> {
  try {
    const raw = await getJSON("/jtwc/warnings.json");
    if (!isRecord(raw) || !Array.isArray(raw["storms"]) || !Array.isArray(raw["features"])) {
      throw new Error("not a JTWC warnings payload");
    }
    const storms: JtwcStorm[] = [];
    for (const s of raw["storms"]) {
      if (!isRecord(s)) continue;
      const lat = num(s, "lat");
      const lon = num(s, "lon");
      if (lat === null || lon === null) continue;
      // The relay writes -1 for "the warning did not say".
      const nz = (k: string): number | null => {
        const v = num(s, k);
        return v === null || v < 0 ? null : v;
      };
      storms.push({
        id: str(s, "id"),
        key: str(s, "key").toLowerCase(),
        basin: str(s, "basin").toUpperCase(),
        number: num(s, "number") ?? 0,
        name: str(s, "name"),
        title: str(s, "title"),
        kind: str(s, "kind"),
        warning: num(s, "warning") ?? 0,
        issued: str(s, "issued"),
        lat,
        lon,
        windKt: nz("wind_kt"),
        moveDeg: nz("move_deg"),
        moveKt: nz("move_kt"),
        hasSwath: s["has_swath"] === true,
      });
    }
    jtwcStorms = storms;
    jtwcFeatures = (raw["features"] as unknown[]).filter(
      (f): f is GeoJSON.Feature => isRecord(f) && f["type"] === "Feature",
    );
    return true;
  } catch (err) {
    console.warn("JTWC warnings unavailable:", err);
    jtwcStorms = [];
    jtwcFeatures = [];
    return false;
  }
}

/** A diagonal hatch tile in the given colour — the swath's fill, so it reads
 *  as "an area" rather than as a second cone. 16 px at pixelRatio 2 = 8 css px. */
function hatchImage(color: string): ImageData {
  const size = 16;
  const c = document.createElement("canvas");
  c.width = size;
  c.height = size;
  const ctx = c.getContext("2d");
  if (ctx === null) return new ImageData(size, size);
  ctx.clearRect(0, 0, size, size);
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.lineCap = "square";
  ctx.beginPath();
  // One diagonal plus the two corner halves, so the tile repeats seamlessly.
  ctx.moveTo(-2, size + 2);
  ctx.lineTo(size + 2, -2);
  ctx.moveTo(-2, size / 2 + 2);
  ctx.lineTo(size / 2 + 2, -2);
  ctx.moveTo(size / 2 - 2, size + 2);
  ctx.lineTo(size + 2, size / 2 - 2);
  ctx.stroke();
  return ctx.getImageData(0, 0, size, size);
}

function ensureHatch(map: MlMap, color: string): void {
  const img = hatchImage(color);
  if (map.hasImage(JTWC_HATCH)) map.updateImage(JTWC_HATCH, img);
  else map.addImage(JTWC_HATCH, img, { pixelRatio: 2 });
}

function jtwcCollection(): GeoJSON.FeatureCollection {
  return { type: "FeatureCollection", features: jtwcFeatures };
}

/** Push the latest warnings into the map source. Safe before the layers exist. */
function applyJTWCData(): void {
  const src = jtwcMap?.getSource(JTWC_SRC) as GeoJSONSource | undefined;
  src?.setData(jtwcCollection());
}

/** One source, five layers filtered by `kind`: the swath (tint + hatch +
 *  edge), the current 34-knot radii, the forecast track and its points. */
function addJTWCLayers(map: MlMap): void {
  jtwcMap = map;
  const crit = cssVar("--crit");
  ensureHatch(map, crit);
  if (map.getSource(JTWC_SRC) === undefined) {
    map.addSource(JTWC_SRC, { type: "geojson", data: jtwcCollection() });
  }
  const before = beforeBuoys(map);
  const kind = (k: string): ExpressionSpecification => ["==", ["get", "kind"], k];
  const add = (layer: LayerSpecification): void => {
    if (!map.getLayer(layer.id)) map.addLayer(layer, before);
  };
  add({
    id: "jtwc-swath",
    type: "fill",
    source: JTWC_SRC,
    filter: kind("swath"),
    paint: { "fill-color": crit, "fill-opacity": 0.08 },
  });
  add({
    id: "jtwc-swath-hatch",
    type: "fill",
    source: JTWC_SRC,
    filter: kind("swath"),
    paint: { "fill-pattern": JTWC_HATCH, "fill-opacity": 0.45 },
  });
  add({
    id: "jtwc-swath-line",
    type: "line",
    source: JTWC_SRC,
    filter: kind("swath"),
    paint: { "line-color": crit, "line-width": 1.5 },
  });
  add({
    id: "jtwc-radii",
    type: "line",
    source: JTWC_SRC,
    filter: ["all", kind("radius"), ["==", ["get", "wind_kt"], 34]],
    paint: {
      "line-color": crit,
      "line-width": 0.8,
      "line-opacity": 0.5,
      "line-dasharray": [2, 2] as unknown as ExpressionSpecification,
    },
  });
  add({
    id: "jtwc-track",
    type: "line",
    source: JTWC_SRC,
    filter: kind("track"),
    paint: { "line-color": crit, "line-width": 2 },
  });
  add({
    id: "jtwc-points",
    type: "circle",
    source: JTWC_SRC,
    filter: ["any", kind("point"), kind("current")],
    paint: {
      "circle-radius": 4.5,
      "circle-color": crit,
      "circle-stroke-color": "#ffffff",
      "circle-stroke-width": 1.2,
    },
  });
  const pop = new maplibregl.Popup({ closeButton: false, closeOnClick: false });
  map.on("mousemove", "jtwc-points", (e) => {
    const p = (e.features?.[0]?.properties ?? {}) as Record<string, unknown>;
    const wind = num(p, "wind_kt");
    const tau = num(p, "tau_h");
    const bits = [
      titleCase(str(p, "title")),
      wind !== null ? `${wind} kt` : "",
      tau === 0 || str(p, "kind") === "current" ? `warning ${str(p, "warning")} position` : `+${tau ?? "?"} h · ${str(p, "label")}`,
    ].filter((b) => b !== "");
    pop.setLngLat(e.lngLat).setText(bits.join(" · ")).addTo(map);
  });
  map.on("mouseleave", "jtwc-points", () => pop.remove());
  // No popup on the swath itself: it is large, it overlaps NHC's outlook areas
  // (two popups stacked on the first try), and what it is belongs on the card
  // and in the help, not under every mouse move across the Philippine Sea.
}

/** Tropical context for a point: inside a development outlook and/or a storm cone. */
export interface TropicalContext {
  dev: { basin: string; prob2day: string; prob7day: string; risk7day: string } | null;
  /** `area` says which shape the point fell in: NHC's probability cone, or
   *  JTWC's gale swath, which is a different claim (see map.tropical help). */
  storm: { name: string; area: "cone" | "gale swath" } | null;
}

function pointInRing(x: number, y: number, ring: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

function pointInPolygon(x: number, y: number, rings: number[][][]): boolean {
  if (rings.length === 0 || !pointInRing(x, y, rings[0])) return false;
  for (let k = 1; k < rings.length; k++) if (pointInRing(x, y, rings[k])) return false; // hole
  return true;
}

function pointInGeometry(lon: number, lat: number, geom: GeoJSON.Geometry | null): boolean {
  if (geom === null) return false;
  if (geom.type === "Polygon") return pointInPolygon(lon, lat, geom.coordinates as number[][][]);
  if (geom.type === "MultiPolygon") {
    return (geom.coordinates as number[][][][]).some((poly) => pointInPolygon(lon, lat, poly));
  }
  return false;
}

/** Everything the tropical feeds currently show, for the Tropical workspace's
 *  storm cards. NHC storms and outlooks come from the SAME module state the map
 *  layers are built from, so the list and the map cannot disagree about what is
 *  active; JTWC-basin storms (onMap: false) are list-only. */
export interface TropicalSummary {
  storms: {
    name: string;
    type: string;
    ss: number | null;
    maxwind: number | null;
    tcdir: number | null;
    tcspd: number | null;
    lat: number | null;
    lon: number | null;
    basin: string;
    /** True for NHC systems: their cone and track are drawn on the map. */
    onMap: boolean;
    /** The advisory the map geometry is from, and when the classification was
     *  last revised. Both printed on the card: these two can be a day apart,
     *  and when they are, only saying so keeps the console honest. */
    advDate: string;
    advNum: string;
    liveAt: string | null;
    /** JTWC-basin storms only: the warning the map's swath and track are from. */
    jtwc: { warning: number; issued: string } | null;
  }[];
  outlooks: {
    basin: string;
    prob2day: string;
    prob7day: string;
    risk7day: string;
    lat: number | null;
    lon: number | null;
  }[];
}

/** Bounding-box centre of a polygon — the fallback outlook marker when the
 *  current-location point layer didn't answer. */
function bboxCenter(geom: GeoJSON.Geometry | null): { lat: number; lon: number } | null {
  if (geom === null || (geom.type !== "Polygon" && geom.type !== "MultiPolygon")) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const rings: number[][][] =
    geom.type === "Polygon"
      ? (geom.coordinates as number[][][])
      : (geom.coordinates as number[][][][]).flat();
  for (const ring of rings) {
    for (const [x, y] of ring) {
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  if (!Number.isFinite(minX)) return null;
  return { lat: (minY + maxY) / 2, lon: (minX + maxX) / 2 };
}

export function tropicalSummary(): TropicalSummary {
  const jtwcByKey = new Map(jtwcStorms.map((j) => [j.key, j] as const));
  const outlooks = tropDevFeatures.map((f) => {
    const p = (f.properties ?? {}) as Record<string, unknown>;
    const oid = num(p, "objectid");
    const loc = (oid !== null ? tropDevPoints.get(oid) : undefined) ?? bboxCenter(f.geometry);
    return {
      basin: str(p, "basin") || "outlook area",
      prob2day: str(p, "prob2day"),
      prob7day: str(p, "prob7day"),
      risk7day: str(p, "risk7day"),
      lat: loc?.lat ?? null,
      lon: loc?.lon ?? null,
    };
  });
  const storms = [
    ...tropStorms.map((s) => ({
      name: s.name,
      type: s.type,
      ss: s.ss,
      maxwind: s.maxwind,
      tcdir: s.tcdir,
      tcspd: s.tcspd,
      lat: s.lat,
      lon: s.lon,
      basin: s.basin,
      onMap: true,
      advDate: s.advDate,
      advNum: s.advNum,
      liveAt: s.liveAt,
      jtwc: null,
    })),
    // JTWC-basin storms: the Esri feed's fix and intensity (15-min fresh) joined
    // to JTWC's own warning for the geometry and the motion, by basin + number.
    ...intlStorms.map((s) => {
      const j = jtwcByKey.get(s.key);
      return {
        name: s.name,
        type: s.type,
        ss: s.ss,
        maxwind: s.maxwind,
        tcdir: j?.moveDeg ?? null,
        tcspd: j?.moveKt ?? null,
        lat: s.lat,
        lon: s.lon,
        basin: s.basin,
        onMap: j !== undefined && j.hasSwath,
        // The Esri aggregate carries no advisory package; its own fixes are the
        // only stamp it has, and they are already ~15 min fresh.
        advDate: "",
        advNum: "",
        liveAt: null,
        jtwc: j === undefined ? null : { warning: j.warning, issued: j.issued },
      };
    }),
    // Warnings the card feed has not got (or the feed is down): a card from
    // the warning itself, so a drawn swath is never an unexplained shape.
    ...jtwcStorms
      .filter((j) => !intlStorms.some((s) => s.key === j.key))
      .map((j) => ({
        name: titleCase(j.name),
        type: titleCase(j.kind),
        ss: null,
        maxwind: j.windKt,
        tcdir: j.moveDeg,
        tcspd: j.moveKt,
        lat: j.lat,
        lon: j.lon,
        basin: j.basin,
        onMap: j.hasSwath,
        advDate: "",
        advNum: "",
        liveAt: null,
        jtwc: { warning: j.warning, issued: j.issued },
      })),
  ];
  return { storms, outlooks };
}

/**
 * Which tropical systems, if any, cover (lat, lon) right now — an NHC development
 * outlook area and/or an active storm's forecast cone. Returns null when clear.
 */
export function tropicalAt(lat: number, lon: number): TropicalContext | null {
  let dev: TropicalContext["dev"] = null;
  for (const f of tropDevFeatures) {
    if (pointInGeometry(lon, lat, f.geometry)) {
      const p = f.properties ?? {};
      dev = {
        basin: str(p, "basin"),
        prob2day: str(p, "prob2day"),
        prob7day: str(p, "prob7day"),
        risk7day: str(p, "risk7day"),
      };
      break;
    }
  }
  let storm: TropicalContext["storm"] = null;
  for (const s of tropStorms) {
    if (s.cones.some((f) => pointInGeometry(lon, lat, f.geometry))) {
      storm = { name: s.name, area: "cone" };
      break;
    }
  }
  if (storm === null) {
    for (const f of jtwcFeatures) {
      const p = (f.properties ?? {}) as Record<string, unknown>;
      if (str(p, "kind") !== "swath") continue;
      if (pointInGeometry(lon, lat, f.geometry)) {
        storm = { name: titleCase(str(p, "name")), area: "gale swath" };
        break;
      }
    }
  }
  return dev === null && storm === null ? null : { dev, storm };
}

// Re-apply the palette to overlay layers whose colours come from CSS vars (buoys,
// tropical). They are built once, so a Day/Dusk/Night switch must re-set them.
export function refreshOverlayColors(map: MlMap): void {
  if (map.getLayer("buoys")) map.setPaintProperty("buoys", "circle-color", cssVar("--warn"));
  const warn = cssVar("--warn");
  const crit = cssVar("--crit");
  const riskColor: ExpressionSpecification = ["match", ["get", "risk7day"], "High", crit, warn];
  if (map.getLayer("trop-dev")) map.setPaintProperty("trop-dev", "fill-color", riskColor);
  if (map.getLayer("trop-dev-line")) map.setPaintProperty("trop-dev-line", "line-color", riskColor);
  if (map.getLayer("trop-cone")) map.setPaintProperty("trop-cone", "fill-color", crit);
  if (map.getLayer("trop-cone-line")) map.setPaintProperty("trop-cone-line", "line-color", crit);
  if (map.getLayer("trop-track")) map.setPaintProperty("trop-track", "line-color", crit);
  if (map.getLayer("trop-points")) map.setPaintProperty("trop-points", "circle-color", crit);
  for (const id of ["jtwc-swath-line", "jtwc-radii", "jtwc-track"]) {
    if (map.getLayer(id)) map.setPaintProperty(id, "line-color", crit);
  }
  if (map.getLayer("jtwc-swath")) map.setPaintProperty("jtwc-swath", "fill-color", crit);
  if (map.getLayer("jtwc-points")) map.setPaintProperty("jtwc-points", "circle-color", crit);
  if (map.getLayer("jtwc-swath-hatch")) ensureHatch(map, crit);
  // Drifter glyph + velocity vector follow the current accent; the tail stays muted.
  if (map.getLayer(DRIFTER_GLYPH)) map.setPaintProperty(DRIFTER_GLYPH, "circle-color", drifterGlyphColor());
  if (map.getLayer(DRIFTER_VEC)) map.setPaintProperty(DRIFTER_VEC, "line-color", drifterGlyphColor());
  if (map.getLayer(DRIFTER_TAIL)) map.setPaintProperty(DRIFTER_TAIL, "line-color", cssVar("--muted"));
  // The field's direction arrows are an SDF, so their re-tint is two paint props.
  refreshArrowColors(map);
  // Port anchors / home ports / AIS targets are baked rasters, not CSS — re-bake
  // them with the current palette so they follow the day/dusk/night switch too.
  void refreshMapIcons(map);
}

// The Esri Living Atlas "Active Hurricanes" live feed — an aggregate of NHC and
// JTWC warnings (observed positions, ~15 min refresh, CORS-open, no key). Used
// ONLY for the basins NHC does not cover: NHC stays authoritative for AL/EP/CP
// (it has cones and full advisories), and filtering those out is the dedupe.
const INTL_OBS =
  "https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/" +
  "Active_Hurricanes_v1/FeatureServer/1/query?where=1%3D1&outFields=*&f=geojson";
const NHC_BASINS = new Set(["al", "ep", "cp"]);

async function loadIntlStorms(): Promise<boolean> {
  try {
    const fc = asFeatureCollection(await getJSON(INTL_OBS));
    if (fc === null) throw new Error("not a FeatureCollection");
    // The layer is a trail of observed fixes per storm; keep the newest per id.
    const latest = new Map<string, { dtg: number; p: Record<string, unknown> }>();
    for (const f of fc.features) {
      const p = (f.properties ?? {}) as Record<string, unknown>;
      const id = str(p, "STORMID");
      const basin = str(p, "BASIN").toLowerCase();
      if (id === "" || basin === "" || NHC_BASINS.has(basin)) continue;
      const dtg = num(p, "DTG") ?? 0;
      const cur = latest.get(id);
      if (cur === undefined || dtg > cur.dtg) latest.set(id, { dtg, p });
    }
    const out: IntlStorm[] = [];
    for (const { p } of latest.values()) {
      const lat = num(p, "LAT");
      const lon = num(p, "LON");
      if (lat === null || lon === null) continue;
      const name = str(p, "STORMNAME");
      out.push({
        // The feed writes "Peilou" / "One-c"; make sure the first letter stands.
        name: name === "" ? str(p, "STORMID").toUpperCase() : name[0].toUpperCase() + name.slice(1),
        type: str(p, "STORMTYPE"),
        ss: num(p, "SS"),
        maxwind: num(p, "INTENSITY"),
        lat,
        lon,
        basin: str(p, "BASIN").toUpperCase(),
        key: str(p, "STORMID").toLowerCase().slice(0, 4),
      });
    }
    intlStorms = out;
    return true;
  } catch (err) {
    console.warn("JTWC-basin storm list unavailable:", err);
    intlStorms = [];
    return false;
  }
}

/**
 * Re-read the two light storm feeds and re-apply them to the cards.
 *
 * DELIBERATELY NOT A FULL RELOAD. The cone and track are map sources built from
 * a MapServer query; rebuilding them means tearing down and re-adding layers
 * under a user who may be mid-pan. What actually goes stale fastest is the
 * classification and intensity, and those are two small JSON reads — so they
 * refresh, and the geometry waits for the next page load. The card says which
 * advisory its cone came from, so the split is visible rather than hidden.
 *
 * Safe before setupTropical has run: both feeds repopulate their own maps, and
 * tropStorms is empty until it does.
 */
export async function refreshTropical(): Promise<void> {
  await Promise.all([loadCurrentStorms(), loadIntlStorms(), loadJTWC()]);
  // The JTWC source is plain GeoJSON, so its geometry CAN refresh in place —
  // unlike the NHC layers, which wait for the next page load.
  applyJTWCData();
  tropStorms = tropStorms.map((st) => {
    const live = currentStorms.get(st.slot.toUpperCase());
    if (live === undefined) return st;
    const merged = mergeCurrentStorm(
      { name: st.name, type: st.type, maxwind: st.maxwind },
      live,
    );
    return { ...st, ...merged, liveAt: live.lastUpdate };
  });
}

export async function setupTropical(
  map: MlMap,
): Promise<{ badge: HTMLElement | null; jtwcOk: boolean; jtwcWarnOk: boolean }> {
  // Kicked off first and awaited last: the two feeds load in parallel, and a
  // dead Esri feed costs the list only its non-NHC storms, never the map layers.
  const intlP = loadIntlStorms();
  const jtwcP = loadJTWC();
  // Awaited before tropStorms is assembled below, because it overrides what
  // goes into it. A failure leaves the map's own classification in place.
  const liveP = loadCurrentStorms();
  // JTWC's warnings get their own block, so a dead NHC service cannot take the
  // western Pacific down with it, and vice versa.
  const jtwcWarnOk = await jtwcP;
  try {
    addJTWCLayers(map);
  } catch (err) {
    console.warn("JTWC layer unavailable:", err);
  }
  try {
    const qfc = (id: number): Promise<GeoJSON.FeatureCollection | null> =>
      getJSON(`${NHC}/${id}/query?where=1%3D1&outFields=*&f=geojson`).then(
        asFeatureCollection,
      );

    const svc = asServiceLayers(await getJSON(`${NHC}?f=json`));
    const coneIds: number[] = [];
    const slotOf: Record<number, string> = {};
    const trackIds: Record<string, number> = {};
    const pointIds: Record<string, number> = {};
    for (const l of svc) {
      const m = l.name.match(/^([A-Z]{2}\d) Forecast (Cone|Track|Points)$/);
      if (!m) continue;
      const slot = m[1];
      const kind = m[2];
      if (kind === "Cone") {
        coneIds.push(l.id);
        slotOf[l.id] = slot;
      } else if (kind === "Track") {
        trackIds[slot] = l.id;
      } else {
        pointIds[slot] = l.id;
      }
    }

    // 3 = the 7-day potential development polygons; 2 = each one's CURRENT
    // location (the X on NHC's graphic) — where the disturbance is, which is
    // what a card thumbnail should mark. Paired by objectid below.
    const [dev, devLoc] = await Promise.all([
      qfc(3).catch(() => null),
      qfc(2).catch(() => null),
    ]);
    const coneResults = await Promise.all(
      coneIds.map((id) =>
        qfc(id)
          .then((g) => ({ id, g }))
          .catch(() => null),
      ),
    );

    const active: { slot: string; cone: GeoJSON.FeatureCollection }[] = [];
    for (const c of coneResults) {
      if (c && c.g && c.g.features.length) {
        active.push({ slot: slotOf[c.id], cone: c.g });
      }
    }

    const tracks: GeoJSON.Feature[] = [];
    const points: GeoJSON.Feature[] = [];
    // The lowest-tau forecast point per slot — the current advisory position and
    // its facts (type, winds, motion), which is what the storm card states.
    const nowPoint = new Map<string, GeoJSON.Feature>();
    await Promise.all(
      active.map(async (s) => {
        const trackId = trackIds[s.slot];
        const pointId = pointIds[s.slot];
        const [t, p] = await Promise.all([
          trackId !== undefined ? qfc(trackId).catch(() => null) : Promise.resolve(null),
          pointId !== undefined ? qfc(pointId).catch(() => null) : Promise.resolve(null),
        ]);
        if (t?.features) tracks.push(...t.features);
        if (p?.features) {
          points.push(...p.features);
          let best: GeoJSON.Feature | null = null;
          let bestTau = Infinity;
          for (const f of p.features) {
            const tau = num((f.properties ?? {}) as Record<string, unknown>, "tau");
            if (tau !== null && tau < bestTau) {
              bestTau = tau;
              best = f;
            }
          }
          if (best !== null) nowPoint.set(s.slot, best);
        }
      }),
    );

    const fc = (feats: GeoJSON.Feature[]): GeoJSON.FeatureCollection => ({
      type: "FeatureCollection",
      features: feats,
    });
    const devFeats = dev?.features ?? [];
    // stash for point-in-area lookup and the storm cards
    tropDevFeatures = devFeats;
    tropDevPoints = new Map();
    for (const f of devLoc?.features ?? []) {
      const oid = num((f.properties ?? {}) as Record<string, unknown>, "objectid");
      const c = pointCoords(f.geometry);
      if (oid !== null && c !== null) tropDevPoints.set(oid, { lat: c[1], lon: c[0] });
    }
    // Settled before the override below reads it.
    await liveP;
    tropStorms = active.map((s) => {
      const now = nowPoint.get(s.slot);
      const np = (now?.properties ?? {}) as Record<string, unknown>;
      const c = now !== undefined ? pointCoords(now.geometry) : null;
      // The live summary wins on classification and intensity where it has this
      // slot; the advisory package keeps geometry, heading and speed, which it
      // alone publishes. Joined on binNumber, which IS the slot ("AT5").
      const merged = mergeCurrentStorm(
        {
          name:
            str(s.cone.features[0]?.properties ?? {}, "stormname") ||
            str(s.cone.features[0]?.properties ?? {}, "stormtype") ||
            `System ${s.slot}`,
          type: str(np, "stormtype"),
          maxwind: num(np, "maxwind"),
        },
        currentStorms.get(s.slot.toUpperCase()),
      );
      return {
        // NHC writes the name WITH its classification — "Tropical Storm
        // Edouard" — so a stale advisory misstates the storm in the card title
        // as well as in the chip. The live bare name replaces it when we have
        // one, and the chip alongside carries the classification.
        name: merged.name,
        cones: s.cone.features,
        lat: c !== null ? c[1] : null,
        lon: c !== null ? c[0] : null,
        type: merged.type,
        ss: num(np, "ssnum"),
        maxwind: merged.maxwind,
        tcdir: num(np, "tcdir"),
        tcspd: num(np, "tcspd"),
        basin: str(np, "basin").toUpperCase(),
        advDate: str(np, "advdate"),
        advNum: str(np, "advisnum"),
        slot: s.slot,
        liveAt: currentStorms.get(s.slot.toUpperCase())?.lastUpdate ?? null,
      };
    });
    map.addSource("trop-dev", { type: "geojson", data: fc(devFeats) });
    map.addSource("trop-cone", {
      type: "geojson",
      data: fc(active.flatMap((s) => s.cone.features)),
    });
    map.addSource("trop-track", { type: "geojson", data: fc(tracks) });
    map.addSource("trop-points", { type: "geojson", data: fc(points) });

    const warn = cssVar("--warn");
    const crit = cssVar("--crit");
    // v1 used --serious for a "High" 7-day risk; the robust palette has no --serious,
    // so High → --crit, everything else → --warn.
    const riskColor: ExpressionSpecification = ["match", ["get", "risk7day"], "High", crit, warn];
    const before = beforeBuoys(map);

    map.addLayer(
      {
        id: "trop-dev",
        type: "fill",
        source: "trop-dev",
        paint: { "fill-color": riskColor, "fill-opacity": 0.18 },
      },
      before,
    );
    map.addLayer(
      {
        id: "trop-dev-line",
        type: "line",
        source: "trop-dev",
        paint: {
          "line-color": riskColor,
          "line-width": 1.5,
          "line-dasharray": [3, 2],
        },
      },
      before,
    );
    map.addLayer(
      {
        id: "trop-cone",
        type: "fill",
        source: "trop-cone",
        paint: { "fill-color": crit, "fill-opacity": 0.13 },
      },
      before,
    );
    map.addLayer(
      {
        id: "trop-cone-line",
        type: "line",
        source: "trop-cone",
        paint: { "line-color": crit, "line-width": 1.5 },
      },
      before,
    );
    map.addLayer(
      {
        id: "trop-track",
        type: "line",
        source: "trop-track",
        paint: { "line-color": crit, "line-width": 2 },
      },
      before,
    );
    map.addLayer(
      {
        id: "trop-points",
        type: "circle",
        source: "trop-points",
        paint: {
          "circle-radius": 4.5,
          "circle-color": crit,
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 1.2,
        },
      },
      before,
    );

    const pop = new maplibregl.Popup({ closeButton: false, closeOnClick: false });
    map.on("mousemove", "trop-dev", (e) => {
      const p = e.features?.[0]?.properties ?? {};
      pop
        .setLngLat(e.lngLat)
        .setText(
          `Tropical development area (${str(p, "basin")}) — 7-day: ${str(
            p,
            "prob7day",
          )} (${str(p, "risk7day")} risk), 2-day: ${str(p, "prob2day")}`,
        )
        .addTo(map);
    });
    map.on("mouseleave", "trop-dev", () => pop.remove());
    map.on("mousemove", "trop-points", (e) => {
      const p = e.features?.[0]?.properties ?? {};
      const name = str(p, "stormname") || str(p, "stormtype") || "Storm";
      const maxwind = str(p, "maxwind");
      const bits = [
        name,
        str(p, "stormtype"),
        maxwind ? `${maxwind} kt` : "",
        str(p, "datelbl") || str(p, "fldatelbl"),
      ].filter((b) => b !== "");
      pop.setLngLat(e.lngLat).setText(bits.join(" · ")).addTo(map);
    });
    map.on("mouseleave", "trop-points", () => pop.remove());

    // A GLYPH, NOT A SENTENCE.
    //
    // This used to print the whole state as text — "NHC tropical: no named
    // storms · 2 development areas watched" — parked over the map. Most days the
    // sentence says nothing happened, and it charged ~260 px of chart for saying
    // it. The cyclone symbol is read at a glance by every mariner alive and needs
    // no width at all.
    //
    // WHAT IS NOT ALLOWED TO SHRINK IS THE COUNT. Collapsing a hurricane
    // indicator to decoration would be a real loss on a console people plan
    // vessel movements from, so the number of active storms stays ON the badge
    // and the badge takes the warning colour while any are up. Quiet days are
    // what gets quieter, not busy ones. The full sentence survives verbatim as
    // the tooltip and the accessible name, and the storm list is one click away.
    const badge = document.createElement("div");
    badge.className = "trop-badge";
    const devCount = devFeats.length;
    const devLabel = `${devCount} development area${devCount > 1 ? "s" : ""}`;
    // The count is every storm drawn on the map, NHC's and JTWC's alike.
    const onMapJtwc = jtwcStorms.filter((j) => j.hasSwath).length;
    const total = active.length + onMapJtwc;
    const state =
      "Tropical: " +
      (total
        ? `${total} active storm${total > 1 ? "s" : ""} (NHC ${active.length}, JTWC ${onMapJtwc})${
            devCount ? ` + ${devLabel}` : ""
          }`
        : devCount
          ? `no named storms · ${devLabel} watched`
          : "no active systems");
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 22 22");
    svg.setAttribute("aria-hidden", "true");
    // Two rotationally symmetric arms and an eye: the tropical cyclone symbol.
    const ARM = "M11 2.6a8.4 8.4 0 0 0-8.4 8.4 5.2 5.2 0 0 1 8.4-4.1z";
    svg.innerHTML =
      `<path d="${ARM}" fill="currentColor"/>` +
      `<path d="${ARM}" fill="currentColor" transform="rotate(180 11 11)"/>` +
      `<circle cx="11" cy="11" r="1.5" fill="currentColor"/>`;
    badge.appendChild(svg);
    if (total > 0) {
      badge.classList.add("on");
      badge.appendChild(
        Object.assign(document.createElement("span"), {
          className: "trop-badge-n",
          textContent: String(total),
        }),
      );
    } else if (devCount > 0) {
      // Something watched but nothing named: worth marking, not worth counting.
      badge.classList.add("watch");
    }
    badge.setAttribute("aria-label", state);
    badge.dataset["state"] = state;
    map.getContainer().appendChild(badge);
    return { badge, jtwcOk: await intlP, jtwcWarnOk };
  } catch (err) {
    console.warn("Tropical layer unavailable:", err);
    return { badge: null, jtwcOk: await intlP, jtwcWarnOk };
  }
}

// ===================================================================================
//  Vessel operational range rings
//
//  When a vessel is selected, draw concentric 1/2/3-day steaming rings around the
//  clicked point at the vessel's ECONOMICAL (cruise) speed — the sustainable
//  transit number, not the spec-sheet maximum. These are straight-line great-
//  circle reaches: they do NOT route around land or account for weather, so they
//  read as "how far this hull can steam in N days", labelled honestly as such.
// ===================================================================================

const RANGE_SRC = "vessel-range";
const RANGE_LINE = "vessel-range-line";

// Rings drawn, and steaming hours per day. Transit runs around the clock, so a
// day is 24 h; the conservatism lives in using cruise (not max) speed.
const RANGE_DAYS = [1, 2, 3];
const STEAM_HOURS_PER_DAY = 24;
const EARTH_NM = 3440.065; // mean Earth radius in nautical miles

// One sequential ramp per ring (near→far), themed by the active display table:
// blue on Day/Dusk, warm amber on Night (saturated blue is suppressed at night).
// Same idea in each — one quantity (distance), stepped in lightness.
const RANGE_TABLES: Record<string, Record<number, string>> = {
  day: { 1: "#12405f", 2: "#2f6f9f", 3: "#8fb8d4" },
  dusk: { 1: "#3f6f8f", 2: "#5a86a0", 3: "#8fb0c4" },
  night: { 1: "#8a5f28", 2: "#c9923f", 3: "#e0b878" },
};
function rangeColor(day: number): string {
  const t =
    typeof document !== "undefined"
      ? document.documentElement.getAttribute("data-theme")
      : null;
  const tbl = RANGE_TABLES[t ?? "day"] ?? RANGE_TABLES["day"];
  return tbl[day] ?? tbl[2];
}
function rangeColorExpr(): ExpressionSpecification {
  return [
    "match",
    ["get", "day"],
    1, rangeColor(1),
    2, rangeColor(2),
    3, rangeColor(3),
    rangeColor(2),
  ] as ExpressionSpecification;
}

// geodesicCircle returns a closed [lon,lat] ring at a constant great-circle
// distance (nm) from a center, via the destination-point formula. Fine for the
// Gulf/Caribbean/West-Africa theatres; a ring spanning the antimeridian would
// need seam handling we don't do here.
function geodesicCircle(lon: number, lat: number, nm: number, steps = 128): number[][] {
  const d = nm / EARTH_NM;
  const lat1 = (lat * Math.PI) / 180;
  const lon1 = (lon * Math.PI) / 180;
  const ring: number[][] = [];
  for (let i = 0; i <= steps; i++) {
    const brng = (i / steps) * 2 * Math.PI;
    const lat2 = Math.asin(
      Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(brng),
    );
    const lon2 =
      lon1 +
      Math.atan2(
        Math.sin(brng) * Math.sin(d) * Math.cos(lat1),
        Math.cos(d) - Math.sin(lat1) * Math.sin(lat2),
      );
    ring.push([((lon2 * 180) / Math.PI + 540) % 360 - 180, (lat2 * 180) / Math.PI]);
  }
  return ring;
}

interface RangeBand {
  day: number;
  nm: number;
}

/**
 * Draw or update the operational range rings around (lat, lon) for a vessel of
 * the given cruise speed, and refresh the overlays-panel legend. A non-positive
 * speed clears both instead. Each ring is coloured by day so the legend swatches
 * identify it — the map draws no text (base styles carry no glyphs).
 */
export function setVesselRange(map: MlMap, lat: number, lon: number, cruiseKn: number): void {
  if (!(cruiseKn > 0)) {
    clearVesselRange(map);
    return;
  }
  const bands: RangeBand[] = RANGE_DAYS.map((day) => ({
    day,
    nm: Math.round(cruiseKn * STEAM_HOURS_PER_DAY * day),
  }));
  const data: GeoJSON.FeatureCollection = {
    type: "FeatureCollection",
    features: RANGE_DAYS.map((day) => ({
      type: "Feature",
      properties: { day },
      geometry: {
        type: "Polygon",
        coordinates: [geodesicCircle(lon, lat, cruiseKn * STEAM_HOURS_PER_DAY * day)],
      },
    })),
  };
  const src = map.getSource(RANGE_SRC);
  if (src && "setData" in src) {
    (src as maplibregl.GeoJSONSource).setData(data);
    // keep ring colours in step with the active display table
    if (map.getLayer(RANGE_LINE)) {
      map.setPaintProperty(RANGE_LINE, "line-color", rangeColorExpr());
    }
  } else {
    map.addSource(RANGE_SRC, { type: "geojson", data });
    map.addLayer({
      id: RANGE_LINE,
      type: "line",
      source: RANGE_SRC,
      paint: {
        "line-color": rangeColorExpr(),
        "line-width": 1.6,
        "line-opacity": 0.9,
        "line-dasharray": [2, 2] as unknown as ExpressionSpecification,
      },
    });
  }
  renderRangeLegend(map, bands);
}

/** Remove the range rings and legend (if any). Safe to call when none exist. */
export function clearVesselRange(map: MlMap): void {
  if (map.getLayer(RANGE_LINE)) map.removeLayer(RANGE_LINE);
  if (map.getSource(RANGE_SRC)) map.removeSource(RANGE_SRC);
  renderRangeLegend(map, null);
}

// ---------- the reach veil ----------
//
// The field paints the whole viewport, and the veil says which part of it is
// YOURS: full colour inside the vessel's 3-day steaming reach, washed out
// beyond it. This is the spatial twin of the lead-time fade — beyond 96 h the
// field desaturates because the forecast stops deserving conviction; beyond the
// 3-day ring it desaturates because the WATER stops being reachable inside the
// planning horizon. Same visual grammar, same reading: muted means "context,
// not decision".
//
// It exists because the alternative boundings are both wrong. Stopping the
// paint at a fetch rectangle shows an edge that means nothing (an
// implementation seam read as a boundary — the complaint that prompted this).
// Stopping it at the reach disc hides the system that will close Thursday's
// window, which today sits OUTSIDE today's reach. Paint everything, dim what
// you cannot get to, and let the 3-day range ring (already drawn by
// setVesselRange, same radius by construction: cruise x 72 h) be the one line
// on the map with a stated meaning.
//
// Two nested holes make a one-step feather: full wash outside 1.15R, half wash
// in the 1.0R..1.15R annulus, clear inside the ring. So even with the range
// rings toggled off there is never a hard unexplained edge — the wash arrives
// gradually, and the ring, when shown, sits exactly on its inner lip.

const VEIL_SRC = "reach-veil";
const VEIL_FILL = "reach-veil-fill";
// One world copy, clear of the poles (the mercator singularity, not a place
// this console works). The field raster only spans its own mesh anyway.
const VEIL_WORLD: number[][] = [
  [-180, -85],
  [180, -85],
  [180, 85],
  [-180, 85],
  [-180, -85],
];

/** Feathered dim beyond the vessel's reach. `radiusNm` is cruise x 72 h — the
 *  same figure the 3-day range ring draws, which is what makes the ring the
 *  veil's labelled edge rather than a second, disagreeing boundary. */
export function setReachVeil(map: MlMap, lat: number, lon: number, radiusNm: number): void {
  if (!(radiusNm > 0)) {
    clearReachVeil(map);
    return;
  }
  const inner = geodesicCircle(lon, lat, radiusNm);
  const feather = geodesicCircle(lon, lat, radiusNm * 1.15);
  const data: GeoJSON.FeatureCollection = {
    type: "FeatureCollection",
    features: [
      { type: "Feature", properties: { band: "far" }, geometry: { type: "Polygon", coordinates: [VEIL_WORLD, feather] } },
      { type: "Feature", properties: { band: "near" }, geometry: { type: "Polygon", coordinates: [feather, inner] } },
    ],
  };
  // The wash is the theme's paper tone: on Day it reads as haze, on Dusk/Night
  // it darkens — both directions say "recede" in that table's own language.
  // Re-set on every call, because the active display table changes at runtime.
  const paper = cssVar("--paper");
  const opacityExpr = [
    "match", ["get", "band"], "near", 0.18, 0.36,
  ] as unknown as ExpressionSpecification;
  const src = map.getSource(VEIL_SRC);
  if (src && "setData" in src) {
    (src as maplibregl.GeoJSONSource).setData(data);
    if (map.getLayer(VEIL_FILL)) {
      map.setPaintProperty(VEIL_FILL, "fill-color", paper);
      map.setPaintProperty(VEIL_FILL, "fill-opacity", opacityExpr);
    }
  } else {
    map.addSource(VEIL_SRC, { type: "geojson", data });
    // Directly under the range rings: above the field raster (which inserts
    // itself below this layer when it exists — see setField's beforeId), below
    // the rings, the site marker and every glyph that must stay crisp.
    const beforeId = map.getLayer(RANGE_LINE) ? RANGE_LINE : undefined;
    map.addLayer(
      {
        id: VEIL_FILL,
        type: "fill",
        source: VEIL_SRC,
        paint: { "fill-color": paper, "fill-opacity": opacityExpr, "fill-antialias": false },
      },
      beforeId,
    );
  }
}

export function clearReachVeil(map: MlMap): void {
  if (map.getLayer(VEIL_FILL)) map.removeLayer(VEIL_FILL);
  if (map.getSource(VEIL_SRC)) map.removeSource(VEIL_SRC);
}

// renderRangeLegend fills the .lc-range-legend slot in the overlays panel with a
// swatch + distance per ring, or hides it when bands is null.
function renderRangeLegend(map: MlMap, bands: RangeBand[] | null): void {
  const box = map.getContainer().querySelector<HTMLElement>(".lc-range-legend");
  if (box === null) return;
  box.replaceChildren();
  if (bands === null) {
    box.style.display = "none";
    return;
  }
  for (const b of bands) {
    const row = document.createElement("div");
    row.className = "lc-rl-row";
    const sw = document.createElement("span");
    sw.className = "lc-swatch";
    sw.style.background = rangeColor(b.day);
    const txt = document.createElement("span");
    txt.textContent = `${b.day} day${b.day > 1 ? "s" : ""} · ${b.nm} nm`;
    row.append(sw, txt);
    box.appendChild(row);
  }
  box.style.display = "flex";
}

// ===================================================================================
//  Jurisdiction & lease overlays (maritime "theaters")
//
//  The ocean is sliced into legal zones (UNCLOS): territorial sea (12 nm), EEZ
//  (200 nm), and disputed / joint-regime areas — which decide whose permit, regulator
//  and flag rules apply. Boundaries from Marine Regions (VLIZ) WMS/WFS; US federal
//  offshore energy leases from BOEM; protected areas from WDPA; whale zones from NOAA.
//  All CORS-clean.
// ===================================================================================

/** Generic transparent raster overlay (WMS or ArcGIS export), hidden until toggled. */
function rasterOverlay(
  map: MlMap,
  id: string,
  tiles: string,
  attribution: string,
  opacity: number,
): void {
  if (map.getSource(id)) return;
  map.addSource(id, { type: "raster", tileSize: 256, attribution, tiles: [tiles] });
  map.addLayer(
    {
      id,
      type: "raster",
      source: id,
      layout: { visibility: "none" },
      paint: { "raster-opacity": opacity },
    },
    beforeBuoys(map),
  );
}

function wmsRaster(map: MlMap, id: string, layer: string, attribution: string): void {
  rasterOverlay(
    map,
    id,
    `${MR}/wms?service=WMS&version=1.1.1&request=GetMap&layers=${layer}` +
      `&styles=&format=image/png&transparent=true&srs=EPSG:3857&width=256&height=256&bbox={bbox-epsg-3857}`,
    attribution,
    0.9,
  );
}

function boemRaster(map: MlMap, id: string, svc: string, show: string): void {
  rasterOverlay(
    map,
    id,
    `https://gis.boem.gov/arcgis/rest/services/BOEM_BSEE/${svc}/MapServer/export` +
      `?bbox={bbox-epsg-3857}&bboxSR=3857&imageSR=3857&size=256,256&layers=show:${show}` +
      `&format=png32&transparent=true&dpi=96&f=image`,
    "US OCS leases: BOEM",
    0.75,
  );
}

/** ArcGIS MapServer export with an attribute filter (layerDefs) — used to show only
 *  the marine/coastal subset of the global protected-area database. */
function arcgisRaster(
  map: MlMap,
  id: string,
  base: string,
  show: string,
  layerDefs: Record<string, string>,
  attribution: string,
  opacity: number,
): void {
  rasterOverlay(
    map,
    id,
    `${base}/export?bbox={bbox-epsg-3857}&bboxSR=3857&imageSR=3857&size=256,256&layers=show:${show}` +
      `&layerDefs=${encodeURIComponent(JSON.stringify(layerDefs))}&format=png32&transparent=true&dpi=96&f=image`,
    attribution,
    opacity,
  );
}

async function geojsonOverlay(
  map: MlMap,
  id: string,
  url: string,
  color: string,
  hover?: (p: Record<string, unknown>) => string,
): Promise<void> {
  if (map.getSource(id)) return;
  const gj = asFeatureCollection(await getJSON(url));
  if (!gj) throw new Error(`${id}: response was not a FeatureCollection`);
  map.addSource(id, { type: "geojson", data: gj });
  map.addLayer(
    {
      id: `${id}-fill`,
      type: "fill",
      source: id,
      layout: { visibility: "none" },
      paint: { "fill-color": color, "fill-opacity": 0.2 },
    },
    beforeBuoys(map),
  );
  map.addLayer(
    {
      id: `${id}-line`,
      type: "line",
      source: id,
      layout: { visibility: "none" },
      paint: {
        "line-color": color,
        "line-width": 1.2,
        "line-dasharray": [2, 1.5],
      },
    },
    beforeBuoys(map),
  );
  if (hover) {
    const pop = new maplibregl.Popup({ closeButton: false, closeOnClick: false });
    map.on("mousemove", `${id}-fill`, (e) => {
      map.getCanvas().style.cursor = "pointer";
      pop.setLngLat(e.lngLat).setHTML(hover(e.features?.[0]?.properties ?? {})).addTo(map);
    });
    map.on("mouseleave", `${id}-fill`, () => {
      map.getCanvas().style.cursor = "";
      pop.remove();
    });
  }
}

interface OverlayDef {
  id: string;
  label: string;
  sub: string;
  /** Swatch colour shown in the layer control (hex, matched to v1). */
  sw: string;
  /** Checked (visible) when the control first renders. */
  defaultOn?: boolean;
  /** Lazily build this overlay's layers (once) and return their layer ids. */
  ensure(map: MlMap): Promise<string[]>;
}

const OVERLAYS: OverlayDef[] = [
  {
    id: "eez",
    label: "EEZ & maritime boundaries",
    sub: "200 nm lines · Marine Regions",
    sw: "#3b6ea5",
    defaultOn: true,
    ensure(map) {
      wmsRaster(map, "ov-eez", "eez_boundaries", "Maritime boundaries: Marine Regions (VLIZ)");
      return Promise.resolve(["ov-eez"]);
    },
  },
  {
    id: "tsea",
    label: "Territorial seas (12 nm)",
    sub: "sovereign waters · Marine Regions",
    sw: "#7aa6c2",
    ensure(map) {
      wmsRaster(map, "ov-tsea", "eez_12nm", "Territorial seas: Marine Regions (VLIZ)");
      return Promise.resolve(["ov-tsea"]);
    },
  },
  {
    id: "disp",
    label: "Disputed / joint-regime zones",
    sub: "overlapping & shared claims",
    sw: "#d94a1f",
    async ensure(map) {
      await geojsonOverlay(
        map,
        "ov-disp",
        `${MR}/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=MarineRegions:eez` +
          `&outputFormat=application/json&srsName=EPSG:4326&propertyName=geoname,pol_type,the_geom` +
          `&cql_filter=${encodeURIComponent("pol_type IN ('Overlapping claim','Joint regime')")}`,
        "#d94a1f",
        (p) =>
          `<strong>${str(p, "geoname")}</strong><br>${str(
            p,
            "pol_type",
          )} — contested/shared jurisdiction`,
      );
      return ["ov-disp-fill", "ov-disp-line"];
    },
  },
  {
    id: "og",
    label: "OCS oil & gas blocks (US)",
    sub: "lease blocks & planning areas · BOEM",
    sw: "#8a6d3b",
    ensure(map) {
      boemRaster(map, "ov-og-goa", "GOA_Layers", "6,7,8");
      boemRaster(map, "ov-og-atl", "ATL_Layers", "4,5");
      return Promise.resolve(["ov-og-goa", "ov-og-atl"]);
    },
  },
  {
    id: "wind",
    label: "Offshore wind leases (US)",
    sub: "active lease areas · BOEM",
    sw: "#0a9396",
    async ensure(map) {
      await geojsonOverlay(
        map,
        "ov-wind",
        "https://services7.arcgis.com/G5Ma95RzqJRPKsWL/ArcGIS/rest/services/Wind_Lease_Boundaries__BOEM_" +
          "/FeatureServer/8/query?where=1%3D1&outFields=LEASE_NUMBER,COMPANY,LEASE_TYPE&f=geojson&geometryPrecision=5",
        "#0a9396",
        (p) => {
          const lease = str(p, "LEASE_NUMBER") || "Wind lease";
          const company = str(p, "COMPANY");
          const leaseType = str(p, "LEASE_TYPE");
          return `<strong>${lease}</strong><br>${company}${leaseType ? ` · ${leaseType}` : ""}`;
        },
      );
      return ["ov-wind-fill", "ov-wind-line"];
    },
  },
  {
    id: "mpa",
    label: "Marine protected areas",
    sub: "global · WDPA (UNEP-WCMC/IUCN)",
    sw: "#2e8b57",
    ensure(map) {
      arcgisRaster(
        map,
        "ov-mpa",
        "https://data-gis.unep-wcmc.org/server/rest/services/ProtectedSites/The_World_Database_of_Protected_Areas/MapServer",
        "1",
        { "1": "realm IN ('Marine','Coastal')" },
        "Protected areas: WDPA (UNEP-WCMC/IUCN)",
        0.55,
      );
      return Promise.resolve(["ov-mpa"]);
    },
  },
  {
    id: "rw",
    label: "Right-whale speed zones (US)",
    sub: "seasonal 10-kn closures · NOAA",
    sw: "#e08a00",
    async ensure(map) {
      await geojsonOverlay(
        map,
        "ov-rw",
        "https://coast.noaa.gov/arcgis/rest/services/OceanReports/NorthAtlanticRightWhaleSeasonalManagementAreas" +
          "/MapServer/0/query?where=1%3D1&outFields=location,restrictedPeriod,restriction&f=geojson&geometryPrecision=5",
        "#e08a00",
        (p) => {
          const loc = str(p, "location") || "Right-whale SMA";
          const restriction = str(p, "restriction") || "Speed restriction";
          const period = str(p, "restrictedPeriod") || "seasonally";
          return (
            `<strong>${loc}</strong><br>${restriction} — 10 kn for vessels ≥ 65 ft<br>` +
            `Active ${period}`
          );
        },
      );
      return ["ov-rw-fill", "ov-rw-line"];
    },
  },
  {
    id: "ports",
    label: "Ports",
    sub: "World Port Index · the published record",
    sw: "#16232e",
    defaultOn: true,
    async ensure(map) {
      await setupPorts(map);
      return [...PORT_LAYERS, "ov-fleet-ports"];
    },
  },
  {
    id: "buoys",
    label: "Buoys",
    sub: "live observation stations",
    sw: "#b9791a",
    // the buoy layer is built at startup by setupBuoys; this entry only toggles it
    ensure() {
      return Promise.resolve(["buoys"]);
    },
  },
  {
    id: "drifters",
    // Named for the quantity, not the instrument (Les, 28 Jul: "add surface
    // currents to Observations" — they were here, filed under jargon).
    label: "Surface currents (drifters)",
    sub: "NOAA GDP observed · drogue 15 m",
    sw: "#2f6f9f",
    // The layers are built lazily here; their DATA is pushed by the console's load()
    // via setDrifterData (site-keyed, like Argo), so toggling on shows whatever the
    // last fetch found near the current site.
    ensure(map) {
      setupDrifters(map);
      return Promise.resolve([DRIFTER_TAIL, DRIFTER_VEC, DRIFTER_GLYPH]);
    },
  },
  {
    id: "fleet",
    label: "Fleet (live AIS)",
    sub: "last heard positions · aisstream",
    sw: "#2f6f9f",
    defaultOn: true,
    async ensure(map) {
      await setupFleet(map);
      return ["ov-fleet-live"];
    },
  },
];

// --- live fleet positions (AIS via the Go /api/fleet endpoint) ----------------

interface FleetVessel {
  mmsi: string;
  name: string;
  lat: number;
  lon: number;
  sogKn: number | null;
  cogDeg: number | null;
  ageSeconds: number;
}

function parseFleet(v: unknown): FleetVessel[] {
  if (!isRecord(v) || !Array.isArray(v["vessels"])) return [];
  const out: FleetVessel[] = [];
  for (const raw of v["vessels"]) {
    if (!isRecord(raw)) continue;
    const lat = raw["lat"];
    const lon = raw["lon"];
    if (typeof lat !== "number" || typeof lon !== "number") continue;
    out.push({
      mmsi: str(raw, "mmsi"),
      name: str(raw, "name"),
      lat,
      lon,
      sogKn: typeof raw["sog_kn"] === "number" ? raw["sog_kn"] : null,
      cogDeg: typeof raw["cog_deg"] === "number" ? raw["cog_deg"] : null,
      ageSeconds: typeof raw["age_seconds"] === "number" ? raw["age_seconds"] : 0,
    });
  }
  return out;
}

function fmtAge(s: number): string {
  if (s < 90) return `${Math.round(s)} s ago`;
  if (s < 5400) return `${Math.round(s / 60)} min ago`;
  return `${(s / 3600).toFixed(1)} h ago`;
}

async function setupFleet(map: MlMap): Promise<void> {
  if (map.getSource("ov-fleet-live") !== undefined) return;
  await registerMapIcons(map);

  const empty: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] };
  map.addSource("ov-fleet-live", { type: "geojson", data: empty });
  // ECDIS convention: the AIS-target triangle points along the course over
  // ground. Deliberately ABOVE buoys/ports — the fleet is what you look for.
  map.addLayer({
    id: "ov-fleet-live",
    type: "symbol",
    source: "ov-fleet-live",
    layout: {
      visibility: "none",
      "icon-image": "ais-target",
      "icon-size": ["interpolate", ["linear"], ["zoom"], 3, 0.55, 8, 0.95] as ExpressionSpecification,
      "icon-rotate": ["coalesce", ["get", "cogDeg"], 0] as ExpressionSpecification,
      "icon-rotation-alignment": "map",
      "icon-allow-overlap": true,
    },
  });

  async function refresh(): Promise<void> {
    try {
      const d = await getJSON("/api/fleet");
      const vessels = parseFleet(d);
      const fc: GeoJSON.FeatureCollection = {
        type: "FeatureCollection",
        features: vessels.map((v) => ({
          type: "Feature",
          geometry: { type: "Point", coordinates: [v.lon, v.lat] },
          properties: { ...v },
        })),
      };
      const src = map.getSource("ov-fleet-live");
      if (src !== undefined && "setData" in src) {
        (src as maplibregl.GeoJSONSource).setData(fc);
      }
    } catch (err) {
      console.warn("Fleet positions unavailable:", err);
    }
  }
  await refresh();
  const iv = window.setInterval(() => void refresh(), 60_000);
  map.once("remove", () => window.clearInterval(iv));

  const pop = new maplibregl.Popup({ closeButton: false, closeOnClick: false });
  const vesselHtml = (p: Record<string, unknown>): string => {
    const sog = typeof p["sogKn"] === "number" ? `${(p["sogKn"] as number).toFixed(1)} kn` : "—";
    const cog = typeof p["cogDeg"] === "number" ? `${Math.round(p["cogDeg"] as number)}°` : "—";
    const age = typeof p["ageSeconds"] === "number" ? fmtAge(p["ageSeconds"] as number) : "";
    return (
      `<strong>${str(p, "name")}</strong> · MMSI ${str(p, "mmsi")}<br>` +
      `SOG ${sog} · COG ${cog} · heard ${age}<br>` +
      `<span style="opacity:0.6">terrestrial AIS — offshore coverage is patchy</span>`
    );
  };
  map.on("mousemove", "ov-fleet-live", (e) => {
    const f = e.features?.[0];
    if (f === undefined) return;
    map.getCanvas().style.cursor = "pointer";
    pop.setLngLat(e.lngLat).setHTML(vesselHtml((f.properties ?? {}) as Record<string, unknown>)).addTo(map);
  });
  map.on("mouseleave", "ov-fleet-live", () => {
    map.getCanvas().style.cursor = "";
    pop.remove();
  });
  map.on("click", "ov-fleet-live", () => {
    clickInterceptor?.(); // clicking your own ship is not a forecast pick
  });
}

// --- Global Drifter Program overlay (observed near-surface currents) ----------
//
// Three layers drawn from one site-keyed fetch (see forecast/drifters.ts): a thin
// TAIL polyline per drifter (the ~5-day track — the point of the whole layer, since
// it paints eddy structure no model plot shows), a short velocity VECTOR from the
// latest fix along the set (length ∝ derived speed), and a GLYPH at the latest
// position. The data is pushed in by the console's load() via setDrifterData; this
// module only owns the map objects. Colours come from CSS vars so the glyph and
// vector re-tint on the Day/Dusk/Night switch, exactly like the buoy icons.
//
// The vector uses --m1 (blue), the SAME accent the Surface-current chart draws its
// line in, so "the current on the map" and "the current on the chart" read as one
// quantity. The tail is --muted: present, but subordinate to the live vector.

const DRIFTER_GLYPH = "ov-drifters"; // circle at latest position (also the click target)
const DRIFTER_VEC = "ov-drifters-vec"; // velocity vector from latest fix
const DRIFTER_TAIL = "ov-drifters-tail"; // ~5-day track polyline
/** Vector length scale: nm drawn per knot of current. Tuned so a 3 kn eddy current
 *  draws a ~24 nm arrow — legible at working Gulf zoom without swamping the glyph. */
const DRIFTER_VEC_NM_PER_KN = 8;
/** Below this the current is drawn as no vector at all: a parked or beached drifter
 *  derives ~0 kn, and a stub arrow on it would imply a set it does not have. */
const DRIFTER_MIN_VEC_KN = 0.05;

/** Endpoint of a drifter's velocity vector: from its latest fix, `speedKn` scaled to
 *  nm, along the set (heading, degrees true, toward). Flat-earth offset — fine at the
 *  tens-of-nm scale of these arrows. */
function drifterVecEnd(d: Drifter): [number, number] | null {
  if (d.speedKn === null || d.headingDeg === null || d.speedKn < DRIFTER_MIN_VEC_KN) return null;
  const lenNm = d.speedKn * DRIFTER_VEC_NM_PER_KN;
  const hdg = (d.headingDeg * Math.PI) / 180;
  const dLat = (lenNm * Math.cos(hdg)) / 60;
  const dLon = (lenNm * Math.sin(hdg)) / (60 * Math.max(0.2, Math.cos((d.lat * Math.PI) / 180)));
  return [d.lon + dLon, d.lat + dLat];
}

function drifterGlyphColor(): string {
  return cssVar("--m1");
}

/** Build the three drifter layers once, hidden, with their click popup. Idempotent —
 *  safe to call from both the layer toggle and setDrifterData. */
export function setupDrifters(map: MlMap): void {
  if (map.getSource(DRIFTER_GLYPH) !== undefined) return;
  const empty: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] };
  const before = beforeBuoys(map);

  // Order matters: tail first (lowest), then vector, then glyph — each inserted just
  // below the buoys layer, so the last added (glyph) ends up topmost of the three.
  map.addSource(DRIFTER_TAIL, { type: "geojson", data: empty });
  map.addLayer(
    {
      id: DRIFTER_TAIL,
      type: "line",
      source: DRIFTER_TAIL,
      layout: { visibility: "none", "line-cap": "round", "line-join": "round" },
      paint: { "line-color": cssVar("--muted"), "line-width": 1.2, "line-opacity": 0.85 },
    },
    before,
  );
  map.addSource(DRIFTER_VEC, { type: "geojson", data: empty });
  map.addLayer(
    {
      id: DRIFTER_VEC,
      type: "line",
      source: DRIFTER_VEC,
      layout: { visibility: "none", "line-cap": "round" },
      paint: { "line-color": drifterGlyphColor(), "line-width": 2 },
    },
    before,
  );
  map.addSource(DRIFTER_GLYPH, { type: "geojson", data: empty });
  map.addLayer(
    {
      id: DRIFTER_GLYPH,
      type: "circle",
      source: DRIFTER_GLYPH,
      layout: { visibility: "none" },
      paint: {
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 3, 3.5, 8, 6] as ExpressionSpecification,
        "circle-color": drifterGlyphColor(),
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 1.2,
      },
    },
    before,
  );

  // Click a drifter → a small popup with id / derived speed / set / fix age. It must
  // NOT move the site marker, so the base map-click is cancelled first (like ports and
  // the fleet). Deliberately no site selection — drifters are DATA, not a place.
  const pop = new maplibregl.Popup({ closeButton: true, closeOnClick: false });
  map.on("click", DRIFTER_GLYPH, (e) => {
    clickInterceptor?.();
    const f = e.features?.[0];
    if (f === undefined) return;
    const at = pointCoords(f.geometry);
    if (at === null) return;
    const p = (f.properties ?? {}) as Record<string, unknown>;
    const id = str(p, "id");
    const speed = typeof p["speedKn"] === "number" ? p["speedKn"] : null;
    const heading = typeof p["headingDeg"] === "number" ? p["headingDeg"] : null;
    const ageH = typeof p["ageHours"] === "number" ? p["ageHours"] : null;
    const sst = typeof p["sstC"] === "number" ? p["sstC"] : null;
    const current =
      speed !== null && heading !== null
        ? `${speed.toFixed(2)} kn setting ${Math.round(heading)}°`
        : "current not resolvable (track too short)";
    const age =
      ageH === null
        ? ""
        : ageH < 1
          ? "just now"
          : ageH < 48
            ? `${Math.round(ageH)} h ago`
            : `${(ageH / 24).toFixed(1)} d ago`;
    pop
      .setLngLat(at)
      .setHTML(
        `<strong>GDP drifter ${id}</strong><br>` +
          `observed current ${current}<br>` +
          (sst !== null ? `SST ${convTemp(sst).toFixed(1)} ${tempUnit()} · ` : "") +
          `fix ${age}<br>` +
          `<span style="opacity:0.6">NOAA Global Drifter Program · drogue 15 m · ` +
          `current derived from position track</span>`,
      )
      .addTo(map);
  });
  map.on("mouseenter", DRIFTER_GLYPH, () => {
    map.getCanvas().style.cursor = "pointer";
  });
  map.on("mouseleave", DRIFTER_GLYPH, () => {
    map.getCanvas().style.cursor = "";
  });
}

/** Push a fetched drifter set onto the map. Builds the layers if the overlay has not
 *  been toggled on yet, so the data is ready the moment it is. The layers keep their
 *  own visibility (toggled by the layer control); this only replaces their data. */
export function setDrifterData(map: MlMap, drifters: Drifter[]): void {
  // load() can push data before the style has settled on the very first site; adding
  // sources/layers then throws. Degrade quietly — the next push (the drifter fetch
  // resolves ~15 s later, long after the map is ready) puts it right.
  if (!map.isStyleLoaded() && map.getSource(DRIFTER_GLYPH) === undefined) return;
  try {
    setDrifterDataInner(map, drifters);
  } catch (err) {
    console.warn("Drifter overlay update skipped:", err);
  }
}

function setDrifterDataInner(map: MlMap, drifters: Drifter[]): void {
  setupDrifters(map);
  const now = Date.now();
  const glyphs: GeoJSON.Feature[] = drifters.map((d) => ({
    type: "Feature",
    geometry: { type: "Point", coordinates: [d.lon, d.lat] },
    properties: {
      id: d.id,
      speedKn: d.speedKn,
      headingDeg: d.headingDeg,
      sstC: d.sstC,
      ageHours: drifterAgeHours(d, now),
    },
  }));
  const vectors: GeoJSON.Feature[] = [];
  const tails: GeoJSON.Feature[] = [];
  for (const d of drifters) {
    const end = drifterVecEnd(d);
    if (end !== null) {
      vectors.push({
        type: "Feature",
        geometry: { type: "LineString", coordinates: [[d.lon, d.lat], end] },
        properties: { id: d.id },
      });
    }
    if (d.tail.length >= 2) {
      tails.push({
        type: "Feature",
        geometry: { type: "LineString", coordinates: d.tail },
        properties: { id: d.id },
      });
    }
  }
  const set = (id: string, feats: GeoJSON.Feature[]): void => {
    const src = map.getSource(id);
    if (src !== undefined && "setData" in src) {
      (src as maplibregl.GeoJSONSource).setData({ type: "FeatureCollection", features: feats });
    }
  };
  set(DRIFTER_GLYPH, glyphs);
  set(DRIFTER_VEC, vectors);
  set(DRIFTER_TAIL, tails);
}

// --- ports (World Port Index) + fleet home ports -----------------------------
//
// This layer SHOWS THE PUBLISHED RECORD AND GRADES NOTHING. Every port draws the
// same neutral anchor. There is no green, no red, and no claim about whether a
// particular ship can enter.
//
// It used to grade. A selected vessel's draft plus an under-keel margin recoloured
// each harbour green/amber/red, and that verdict was withdrawn because the inputs
// it needed are inputs we do not hold. Four of them, each on its own decisive:
//
//   - A surveyed controlling depth. Pub 150 publishes a CODED LETTER — a band
//     roughly five feet wide — which we decode to its LOWER BOUND. The real depth
//     is somewhere above the figure, by an unstated amount, of unstated vintage.
//   - A sailing draft. Two of the six fleet vessels carry a null draft, and the
//     rest are spec-sheet figures, not the ship as she floats today.
//   - The offset between mean sea level and the local chart datum. Outside US
//     waters that is ±0.5 m, which is larger than the entire clearance margin the
//     verdict used to add.
//   - Tide, and squat. We model neither.
//
// CONVENTIONS.md, "Errors & honesty": *"We cannot say" is a verdict, and it
// outranks a hedged yes. Withhold rather than qualify when an input we do not hold
// would change the answer.* A green anchor beside a named vessel's name is
// functionally advice to enter. Publishing the harbour's own record is not.
//
// docs/PORTS.md remains the roadmap for reinstating a verdict once those four
// inputs are in hand. Until then the card's job is to present the record clearly
// and label it honestly — channel and anchorage stated SEPARATELY (they answer
// different questions), each as the band floor it is.

const WPI_BASE =
  "https://services9.arcgis.com/j1CY4yzWfwptbTWN/ArcGIS/rest/services/WorldPortIndex_WFL1" +
  "/FeatureServer/0/query";
// The whole index, not just the big harbours. `HARBORSIZE IN ('L','M')` drew 521
// of 3,669 ports and excluded EVERY ONE of the fleet's bases — Freeport TX and
// Coxen Hole are 'V', Monrovia and Lake Charles are 'S', and Cameron LA is not in
// the index at all. A layer that cannot speak about the ports we sail from is
// answering someone else's question. 3,669 features is ~110 KB gzipped over two
// pages; the small harbours are drawn quietly and only once you have zoomed in.
const WPI_FIELDS =
  "OBJECTID,PORT_NAME,COUNTRY,HARBORSIZE,CHAN_DEPTH,ANCH_DEPTH,ENTRY_TIDE";
const WPI_PAGE = 2000; // the service's transfer limit — paged, not truncated

function wpiPageUrl(offset: number): string {
  return (
    `${WPI_BASE}?where=1%3D1&outFields=${WPI_FIELDS}&f=geojson` +
    `&orderByFields=OBJECTID&resultRecordCount=${WPI_PAGE}&resultOffset=${offset}`
  );
}

/** The one anchor every WPI port draws. A single neutral glyph is the whole
 *  point: the map ranks no harbour above another, because we hold nothing that
 *  would justify the ranking. The graded glyphs it replaced are gone from
 *  `ui/mapicons.ts` too — this is the only port anchor that exists now. */
const PORT_ICON = "port-navy";

// A layer click (port/fleet) must stop the console's base map-click from
// moving the marker and reloading the forecast. The console registers a
// canceller here; layer click handlers invoke it first.
let clickInterceptor: (() => void) | null = null;
export function setMapClickInterceptor(cb: () => void): void {
  clickInterceptor = cb;
}

/** Pub 150 depth code → lower-bound feet ("I" unused; blank = not charted).
 *
 *  Sixteen letters, each a band five feet wide, running DOWN the alphabet as the
 *  water gets shallower: `Q` is 0–5 ft, `P` is 6–10, … `B` is 71–75. `A` is the
 *  open-ended top band — 76 ft and deeper, with no upper limit at all. */
const DEPTH_CODE_FT: Record<string, number> = {
  A: 76, B: 71, C: 66, D: 61, E: 56, F: 51, G: 46, H: 41,
  J: 36, K: 31, L: 26, M: 21, N: 16, O: 11, P: 6, Q: 0,
};

/** Decode a Pub 150 depth letter to metres — **the FLOOR of its band, i.e. a
 *  LOWER BOUND**, never the depth itself. Returns `-1` for a blank or unknown
 *  code, meaning *not charted*, which is a different thing from zero depth.
 *
 *  Read `depthCodeToM("L") === 7.9` as "26 ft or more", not "26 ft": the true
 *  figure sits somewhere in a five-foot band above it, and `A` has no ceiling.
 *  The lower bound is the conservative choice per CONVENTIONS.md — uncertainty
 *  spent against the reader — and every number the port card prints is one of
 *  these, which is why the card says "at least" in front of each and why this
 *  function is exported and pinned by test rather than left private. */
export function depthCodeToM(code: string): number {
  const ft = DEPTH_CODE_FT[code.trim().toUpperCase()];
  return ft === undefined ? -1 : Math.round(ft * 0.3048 * 10) / 10;
}

/** A decoded WPI depth for display. The source codes are feet, so imperial mode
 *  recovers the original whole-foot bound (76 ft, not 23.2 m re-converted to
 *  76.1 ft); metric keeps the tenth-of-a-metre decode as before. */
function fmtPortDepth(m: number): string {
  return getUnitSystem() === "imperial" ? `${Math.round(m / 0.3048)} ft` : `${m} m`;
}

/** The client's staging bases (from the TDI-Brooks research; per-vessel home
 *  ports belong in the vessels table eventually — this is the bespoke config). */
const FLEET_PORTS = [
  { name: "Cameron, LA", vessel: "R/V Brooks McCall", lat: 29.7977, lon: -93.3251 },
  { name: "Freeport, TX", vessel: "R/V Proteus", lat: 28.948, lon: -95.308 },
  { name: "Coxen Hole (Roatán), HN", vessel: "R/V Miss Emma McCall", lat: 16.3167, lon: -86.5333 },
  { name: "Monrovia, LR", vessel: "R/V Gyre (current campaign)", lat: 6.3506, lon: -10.7972 },
] as const;

/** How close a WPI record must sit to a fleet pin before we call it the same
 *  harbour and draw ONE marker instead of two. Generous enough to absorb the
 *  index's coarse whole-minute positions, tight enough that Cameron does not
 *  swallow Lake Charles — the two are 47 km apart up the Calcasieu channel and
 *  are genuinely different places. */
const HOME_PORT_MATCH_KM = 15;

function roughKm(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const dLat = (aLat - bLat) * 111.32;
  const dLon = (aLon - bLon) * 111.32 * Math.cos(((aLat + bLat) / 2) * (Math.PI / 180));
  return Math.hypot(dLat, dLon);
}

/** Both port layers share the icon — the big harbours and the quiet small ones
 *  differ only in size and when they appear.
 *
 *  There is deliberately no `setPortVessel` here any more, and nothing in this
 *  file reads the selected vessel. The layer's appearance is a pure function of
 *  the World Port Index record; changing the vessel in the workability panel
 *  changes nothing on the map, because the map is no longer making a claim that
 *  a vessel could be party to. If a verdict is ever reinstated (docs/PORTS.md),
 *  the seam it needs is a setter here plus a re-push of `icon-image` across both
 *  of these layers. */
const PORT_LAYERS = ["ov-ports", "ov-ports-small"] as const;

/** The decoded World Port Index, fetched at most once per session.
 *
 *  Two callers now want it and neither should pay for the other: the ports
 *  LAYER, which is off until somebody switches it on, and the map SEARCH, which
 *  needs harbour names whether or not any port is drawn. Sharing one promise
 *  means the index is fetched on whichever comes first and reused by the second
 *  — a search does not turn a layer on, and turning the layer on does not fetch
 *  3,669 ports a second time. */
let wpiOnce: Promise<GeoJSON.FeatureCollection> | null = null;

function loadWpi(): Promise<GeoJSON.FeatureCollection> {
  wpiOnce ??= fetchWpi().catch((err: unknown) => {
    // Do not cache a failure: a port index that failed once because the vessel
    // was between satellites should be retried, not written off for the session.
    wpiOnce = null;
    throw err;
  });
  return wpiOnce;
}

/** A harbour as the search sees it — name, country, position. */
export interface PortPlace {
  name: string;
  country: string;
  lat: number;
  lon: number;
  /** A base this fleet sails from, which the search floats above equal matches. */
  home: boolean;
}

/** Every WPI harbour, for name search. Empty (never throwing) if the index is
 *  unreachable: search that quietly loses its ports is a smaller failure than
 *  search that stops working, and the waypoints still answer. */
export async function portIndex(): Promise<PortPlace[]> {
  let fc: GeoJSON.FeatureCollection;
  try {
    fc = await loadWpi();
  } catch (err) {
    console.warn("Port search unavailable:", err);
    return [];
  }
  const out: PortPlace[] = [];
  const claimed = new Set<string>();
  for (const f of fc.features) {
    const at = pointCoords(f.geometry);
    if (at === null) continue;
    const p = (f.properties ?? {}) as Record<string, unknown>;
    const name = typeof p.PORT_NAME === "string" ? p.PORT_NAME.trim() : "";
    if (name === "") continue;
    const hn = p.homeName;
    if (typeof hn === "string") claimed.add(hn);
    out.push({
      name,
      country: typeof p.COUNTRY === "string" ? p.COUNTRY.trim() : "",
      lat: at[1],
      lon: at[0],
      home: p.home === true,
    });
  }
  // The bases the index does not contain. Cameron LA is not in Pub 150 at all —
  // it is where the Brooks McCall sails from and searching for it found nothing,
  // which is the one result this feature must never give. Fleet pins are drawn on
  // the map for exactly this reason; the search now knows about them too.
  for (const hp of FLEET_PORTS) {
    if (claimed.has(hp.name)) continue;
    out.push({ name: hp.name, country: hp.vessel, lat: hp.lat, lon: hp.lon, home: true });
  }
  return out;
}

async function fetchWpi(): Promise<GeoJSON.FeatureCollection> {
  const res = await fetch(wpiPageUrl(0));
  if (!res.ok) throw new Error(`WPI HTTP ${res.status}`);
  const gj = (await res.json()) as unknown;
  const fc = asFeatureCollection(gj);
  if (fc === null) throw new Error("WPI: unexpected response");

  // The service caps a response at WPI_PAGE features, so the rest of the index
  // arrives by offset. A failed later page is not fatal: we draw what we have
  // and warn, the same graceful-degradation rule every other overlay follows.
  for (let offset = WPI_PAGE; fc.features.length === offset; offset += WPI_PAGE) {
    try {
      const more = await fetch(wpiPageUrl(offset));
      if (!more.ok) throw new Error(`WPI HTTP ${more.status}`);
      const page = asFeatureCollection((await more.json()) as unknown);
      if (page === null || page.features.length === 0) break;
      fc.features.push(...page.features);
    } catch (err) {
      console.warn("WPI: partial port index —", err);
      break;
    }
  }

  // Decode once, into the properties the hover card reads:
  //   chanDepthM  the CHANNEL band floor, -1 = not charted
  //   anchDepthM  the ANCHORAGE band floor, -1 = not charted
  //   tideGated   the index's own ENTRY_TIDE flag, stated in words on the card
  //   home        this harbour IS a fleet base — drawn big whatever its size
  //
  // The two depths are carried SEPARATELY and no code in this file combines
  // them. The old single `minDepthM = Math.max(chan, anch)` was wrong in a way
  // that mattered: an anchorage is where you WAIT OUTSIDE a harbour, so a deep
  // anchorage cannot rescue a shallow channel, and taking the maximum overstated
  // the entry depth at 1,986 of the 3,669 ports in the index. Monrovia — a fleet
  // base — read 12.5 m of anchorage over a 7.9 m channel. They answer different
  // questions and the card asks them separately.
  for (const f of fc.features) {
    const p = (f.properties ?? {}) as Record<string, unknown>;
    p.chanDepthM = depthCodeToM(typeof p.CHAN_DEPTH === "string" ? p.CHAN_DEPTH : "");
    p.anchDepthM = depthCodeToM(typeof p.ANCH_DEPTH === "string" ? p.ANCH_DEPTH : "");
    p.tideGated =
      typeof p.ENTRY_TIDE === "string" && p.ENTRY_TIDE.trim().toUpperCase() === "Y";
    f.properties = p;
  }

  // One marker per harbour. Where a fleet base has a WPI record, the record wins
  // — it carries depth, the tide flag and the full card, which a bare pin never
  // did — and the pin stands down. Cameron LA has no record anywhere in the
  // index, so it keeps its pin and is the only one left.
  const claimed = new Set<string>();
  for (const f of fc.features) {
    const at = pointCoords(f.geometry);
    if (at === null) continue;
    const hp = FLEET_PORTS.find(
      (h) => !claimed.has(h.name) && roughKm(h.lat, h.lon, at[1], at[0]) <= HOME_PORT_MATCH_KM,
    );
    if (hp === undefined) continue;
    claimed.add(hp.name);
    const p = (f.properties ?? {}) as Record<string, unknown>;
    p.home = true;
    p.homeVessel = hp.vessel;
    p.homeName = hp.name;
    f.properties = p;
  }

  return fc as GeoJSON.FeatureCollection;
}

async function setupPorts(map: MlMap): Promise<void> {
  if (map.getSource("ov-ports") !== undefined) return;
  await registerMapIcons(map);
  const fc = await loadWpi();

  map.addSource("ov-ports", { type: "geojson", data: fc });
  const bigHarbour = ["in", ["get", "HARBORSIZE"], ["literal", ["L", "M"]]];
  const isHome = ["==", ["get", "home"], true];
  map.addLayer(
    {
      id: "ov-ports",
      type: "symbol",
      source: "ov-ports",
      // large and medium harbours, plus every fleet base regardless of its size
      filter: ["any", bigHarbour, isHome] as unknown as ExpressionSpecification,
      layout: {
        visibility: "none",
        "icon-image": PORT_ICON,
        "icon-size": ["interpolate", ["linear"], ["zoom"], 3, 0.45, 8, 0.85] as ExpressionSpecification,
        "icon-allow-overlap": true,
      },
    },
    beforeBuoys(map),
  );
  // The other 3,100-odd — small, very small and unclassified harbours. Drawing
  // them at world zoom is a wall of anchors, so they wait for zoom 5 and come in
  // smaller and half-lit: present when you are looking at a coast, quiet when
  // you are looking at an ocean.
  map.addLayer(
    {
      id: "ov-ports-small",
      type: "symbol",
      source: "ov-ports",
      minzoom: 5,
      filter: ["all", ["!", bigHarbour], ["!", isHome]] as unknown as ExpressionSpecification,
      layout: {
        visibility: "none",
        "icon-image": PORT_ICON,
        "icon-size": ["interpolate", ["linear"], ["zoom"], 5, 0.32, 9, 0.6] as ExpressionSpecification,
        "icon-allow-overlap": false,
      },
      paint: { "icon-opacity": 0.72 },
    },
    beforeBuoys(map),
  );

  // Which bases a WPI record already speaks for. The claim is stamped onto the
  // features during the fetch, so it is read back from them here rather than
  // passed along — one source of truth, and the pin layer cannot drift out of
  // step with the harbour layer it is meant to complement.
  const claimed = new Set<string>();
  for (const f of fc.features) {
    const hn = (f.properties ?? {})["homeName"];
    if (typeof hn === "string") claimed.add(hn);
  }

  const fleetFc: GeoJSON.FeatureCollection = {
    type: "FeatureCollection",
    features: FLEET_PORTS.filter((hp) => !claimed.has(hp.name)).map((hp) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: [hp.lon, hp.lat] },
      properties: { name: hp.name, vessel: hp.vessel },
    })),
  };
  map.addSource("ov-fleet-ports", { type: "geojson", data: fleetFc });
  map.addLayer(
    {
      id: "ov-fleet-ports",
      type: "symbol",
      source: "ov-fleet-ports",
      layout: {
        visibility: "none",
        "icon-image": "home-port",
        "icon-size": ["interpolate", ["linear"], ["zoom"], 3, 0.55, 8, 0.95] as ExpressionSpecification,
        "icon-allow-overlap": true,
      },
    },
    beforeBuoys(map),
  );

  const pop = new maplibregl.Popup({ closeButton: false, closeOnClick: false });
  for (const id of PORT_LAYERS) {
    map.on("mousemove", id, (e) => {
      const f = e.features?.[0];
      if (f === undefined) return;
      map.getCanvas().style.cursor = "pointer";
      pop.setLngLat(e.lngLat).setHTML(portHoverHtml((f.properties ?? {}) as Record<string, unknown>)).addTo(map);
    });
    map.on("mouseleave", id, () => {
      map.getCanvas().style.cursor = "";
      pop.remove();
    });
  }
  map.on("mousemove", "ov-fleet-ports", (e) => {
    const f = e.features?.[0];
    if (f === undefined) return;
    map.getCanvas().style.cursor = "pointer";
    const p = (f.properties ?? {}) as Record<string, unknown>;
    pop
      .setLngLat(e.lngLat)
      .setHTML(
        `<strong>Home port — ${typeof p.vessel === "string" ? p.vessel : ""}</strong><br>` +
          `${typeof p.name === "string" ? p.name : ""} (staging base)`,
      )
      .addTo(map);
  });
  map.on("mouseleave", "ov-fleet-ports", () => {
    map.getCanvas().style.cursor = "";
    pop.remove();
  });

  // click a port → fetch its FULL World Port Index record and show everything
  const detail = new maplibregl.Popup({ closeButton: true, maxWidth: "360px", className: "portcard" });
  for (const id of PORT_LAYERS) {
    map.on("click", id, (e) => {
      clickInterceptor?.();
      const f = e.features?.[0];
      if (f === undefined) return;
      const p = (f.properties ?? {}) as Record<string, unknown>;
      const oid = typeof p.OBJECTID === "number" ? p.OBJECTID : Number(p.OBJECTID);
      if (!Number.isFinite(oid)) return;
      const home = typeof p.homeVessel === "string" ? p.homeVessel : null;
      const at = e.lngLat;
      detail.setLngLat(at).setHTML(`<p class="quiet">Loading port record…</p>`).addTo(map);
      void fetchPortRecord(oid, home).then((html) => {
        if (html !== null && detail.isOpen()) detail.setHTML(html);
      });
    });
  }
}

// --- full WPI record rendering ------------------------------------------------

const HARBOR_SIZE: Record<string, string> = { L: "large", M: "medium", S: "small", V: "very small" };
const HARBOR_TYPE: Record<string, string> = {
  CN: "coastal, natural", CB: "coastal, breakwater", CT: "coastal, tide gate",
  RN: "river, natural", RB: "river, basin", RT: "river, tide gate",
  LC: "lake or canal", OR: "open roadstead", TH: "typhoon harbor",
};
const SHELTER: Record<string, string> = { E: "excellent", G: "good", F: "fair", P: "poor", N: "none" };
const REPAIRS: Record<string, string> = { A: "major", B: "moderate", C: "limited", D: "emergency only", N: "none" };
const RAILWAY_SIZE: Record<string, string> = { S: "small", M: "medium", L: "large" };

/** What the depth numbers on the card actually are. Every one of them is a
 *  Pub 150 coded LETTER read at the floor of its five-foot band, so each is a
 *  lower bound with an unstated amount of water above it, of unstated survey
 *  vintage, at a chart datum that varies by country. This note now carries more
 *  weight than it used to: the card no longer draws a conclusion from these
 *  numbers, so the reader draws their own, and must know what they are holding. */
const DEPTH_BAND_NOTE =
  "Every depth above is a Pub 150 coded band read at its FLOOR — a lower bound about " +
  "5 ft wide, not a surveyed controlling depth. Survey vintage is not published, and " +
  "the chart datum varies by country.";

/** Why the card states a record and stops. Kept as one sentence in the console's
 *  voice: the four things we would need before a fit could honestly be called. */
const NO_VERDICT_NOTE =
  "SeaState does not judge whether a vessel fits. That needs a surveyed controlling " +
  "depth, the ship's sailing draft, the local chart datum offset and the tide — we hold none of them.";

/** The tide sentence. We have NO tide time series and compute no window — the
 *  only honest thing to say is that the index flags entry as tide-dependent and
 *  that we do not model it. It is a plain stated fact on the card now, in words,
 *  and no longer a colour on the map. */
const TIDE_GATED_NOTE = "Entry depends on the state of the tide — SeaState does not model tide.";

/** The hover card for a port anchor, in either port layer.
 *
 *  Channel and anchorage get a line each and are never merged. A hover is the
 *  glance version of the card, and the one thing a glance must not do is hand
 *  back a single "depth" that is really the deeper of two different places. */
function portHoverHtml(p: Record<string, unknown>): string {
  const name = typeof p.PORT_NAME === "string" ? p.PORT_NAME : "Port";
  const country = typeof p.COUNTRY === "string" ? p.COUNTRY : "";
  const sizeCode = typeof p.HARBORSIZE === "string" ? p.HARBORSIZE.trim().toUpperCase() : "";
  const size = HARBOR_SIZE[sizeCode] !== undefined ? `${HARBOR_SIZE[sizeCode]} harbor` : "harbor";
  const chan = typeof p.chanDepthM === "number" ? p.chanDepthM : -1;
  const anch = typeof p.anchDepthM === "number" ? p.anchDepthM : -1;
  const tideGated = p.tideGated === true;

  const lines: string[] = [size];
  lines.push(chan >= 0 ? `channel at least ${fmtPortDepth(chan)}` : "channel depth not charted");
  lines.push(
    anch >= 0 ? `anchorage at least ${fmtPortDepth(anch)}` : "anchorage depth not charted",
  );
  if (typeof p.homeVessel === "string") lines.push(`home port — ${p.homeVessel}`);
  if (tideGated) lines.push(`<strong>entry is tide-dependent</strong> — tide not modelled`);

  return (
    `<strong>${name}</strong> ${country}<br>${lines.join("<br>")}` +
    `<br><span style="opacity:0.6">Pub 150 coded bands, lower bound — not for navigation</span>`
  );
}

function yes(a: Record<string, unknown>, key: string): boolean {
  return typeof a[key] === "string" && (a[key] as string).trim().toUpperCase() === "Y";
}
function code(a: Record<string, unknown>, key: string, table: Record<string, string>): string {
  const v = a[key];
  return typeof v === "string" ? (table[v.trim().toUpperCase()] ?? "") : "";
}
/** One decoded depth, as its own labelled line. Shows BOTH the Pub 150 letter
 *  the index actually publishes and the metres we decode it to, because the
 *  letter is the record and the number is our arithmetic on it — and prefixes
 *  the number with "at least", since the band floor is a lower bound and reading
 *  it as the depth is precisely the mistake this card exists to prevent.
 *
 *  `where` names what the figure is a depth OF. An anchorage is outside the
 *  harbour; a channel is the thing you must fit through. They are not
 *  interchangeable and the card never lets them look it. */
function depthLine(
  a: Record<string, unknown>,
  key: string,
  label: string,
  where = "",
  /** State the absence rather than dropping the row. The channel and anchorage
   *  use this: 320 ports in the index carry an anchorage and NO channel, and a
   *  card that simply omits the channel line reads as though there were nothing
   *  to report. An uncharted channel is a fact about the port worth printing —
   *  and it is the fact the old combined depth used to paper over by falling
   *  back on the anchorage. Piers and terminals stay optional. */
  sayWhenAbsent = false,
): string {
  const raw = typeof a[key] === "string" ? (a[key] as string).trim().toUpperCase() : "";
  const m = depthCodeToM(raw);
  const site = where === "" ? "" : ` <span class="quiet">(${where})</span>`;
  if (m < 0) {
    return sayWhenAbsent
      ? `<strong>${label}</strong>${site} — <span class="quiet">not charted in the index</span>`
      : "";
  }
  return `<strong>${label}</strong>${site} — band <code>${raw}</code>, at least ${fmtPortDepth(m)}`;
}

async function fetchPortRecord(objectId: number, homeVessel: string | null): Promise<string | null> {
  try {
    const d = await getJSON(`${WPI_BASE}?where=OBJECTID%3D${objectId}&outFields=*&f=json`);
    if (!isRecord(d) || !Array.isArray(d["features"])) return null;
    const first: unknown = d["features"][0];
    if (!isRecord(first) || !isRecord(first["attributes"])) return null;
    const a = first["attributes"] as Record<string, unknown>;

    const sec = (title: string, items: string[]): string => {
      const kept = items.filter((s) => s !== "");
      return kept.length > 0
        ? `<div class="port-sec">${title}</div><div>${kept.join(" · ")}</div>`
        : "";
    };
    /** Same, but one item per LINE. The depths use this: run through a "·" the
     *  way the services list does and channel, anchorage and pier read as one
     *  run-on figure, which is the reading the whole change is here to stop. */
    const secLines = (title: string, items: string[]): string => {
      const kept = items.filter((s) => s !== "");
      return kept.length > 0
        ? `<div class="port-sec">${title}</div><div>${kept.join("<br>")}</div>`
        : "";
    };

    const entryRestrictions = [
      yes(a, "ENTRY_TIDE") ? "tide" : "",
      yes(a, "ENTRYSWELL") ? "swell" : "",
      yes(a, "ENTRY_ICE") ? "ice" : "",
      yes(a, "ENTRYOTHER") ? "other" : "",
    ].filter((s) => s !== "");
    const cranes = [
      yes(a, "CRANEFIXED") ? "fixed" : "",
      yes(a, "CRANEMOBIL") ? "mobile" : "",
      yes(a, "CRANEFLOAT") ? "floating" : "",
    ].filter((s) => s !== "");
    const lifts = [
      yes(a, "LIFT_100_") ? ">100 t" : "",
      yes(a, "LIFT50_100") ? "50–100 t" : "",
      yes(a, "LIFT_25_49") ? "25–49 t" : "",
      yes(a, "LIFT_0_24") ? "<25 t" : "",
    ].filter((s) => s !== "");
    const comms = [
      yes(a, "COMM_VHF") ? "VHF" : "",
      yes(a, "COMM_RADIO") ? "radio" : "",
      yes(a, "COMM_PHONE") ? "phone" : "",
      yes(a, "COMM_AIR") ? "air link" : "",
      yes(a, "COMM_RAIL") ? "rail link" : "",
    ].filter((s) => s !== "");

    const name = typeof a.PORT_NAME === "string" ? a.PORT_NAME : "Port";
    const country = typeof a.COUNTRY === "string" ? a.COUNTRY : "";
    const headBits = [
      code(a, "HARBORSIZE", HARBOR_SIZE) !== "" ? `${code(a, "HARBORSIZE", HARBOR_SIZE)} harbor` : "",
      code(a, "HARBORTYPE", HARBOR_TYPE),
      code(a, "SHELTER", SHELTER) !== "" ? `shelter ${code(a, "SHELTER", SHELTER)}` : "",
      yes(a, "PORTOFENTR") ? "port of entry" : "",
    ].filter((s) => s !== "");

    const tideGated = yes(a, "ENTRY_TIDE");

    const home =
      homeVessel !== null
        ? `<div class="port-fit">Fleet home port — ${homeVessel}</div>`
        : "";

    // Tide-dependent entry is a property of the PORT and the index says so in
    // its own field, so the card says it in words. It used to be carried by an
    // amber anchor as well; the anchor is neutral now, which makes this sentence
    // the only place the fact lives and therefore not optional.
    const tideNote = tideGated
      ? `<div class="port-fit">Entry is tide-dependent — the index flags this port ` +
        `<code>ENTRY_TIDE&nbsp;=&nbsp;Y</code>. ${TIDE_GATED_NOTE} No window is computed ` +
        `here; use the tide tables and the pilot.</div>`
      : "";

    const maxVessel =
      a.MAX_VESSEL === "L" ? "max vessel over 500 ft" : a.MAX_VESSEL === "M" ? "max vessel ≤ 500 ft" : "";
    const tide =
      typeof a.TIDE_RANGE === "number" && a.TIDE_RANGE > 0 ? `tide range ${fmtLimitLen(a.TIDE_RANGE)}` : "";

    return (
      `<div class="port-head"><strong>${name}</strong> ${country}</div>` +
      `<div>${headBits.join(" · ")}</div>` +
      home +
      tideNote +
      // Depths first and one per line, each naming what it is a depth OF. The
      // channel is what you transit; the anchorage is where you wait outside.
      // Nothing here combines them into a single "port depth", because the two
      // answer different questions and the deeper of the two answers neither.
      secLines("Depths — Pub 150 coded bands, lower bound", [
        depthLine(a, "CHAN_DEPTH", "Channel", "the approach you transit", true),
        depthLine(a, "ANCH_DEPTH", "Anchorage", "where you wait, outside the harbour", true),
        depthLine(a, "CARGODEPTH", "Cargo pier", "alongside"),
        depthLine(a, "OIL_DEPTH", "Oil terminal", "alongside"),
      ]) +
      sec("Entry", [
        maxVessel,
        tide,
        yes(a, "OVERHD_LIM") ? "overhead limits" : "",
        entryRestrictions.length > 0 ? `entry restricted by ${entryRestrictions.join("/")}` : "",
        yes(a, "TURN_BASIN") ? "turning basin" : "",
        yes(a, "HOLDGROUND") ? "good holding ground" : "",
      ]) +
      sec("Services", [
        yes(a, "PILOT_REQD") ? "pilot required" : "",
        yes(a, "PILOTAVAIL") ? "pilots available" : "",
        yes(a, "TUG_ASSIST") ? "tug assist" : "",
        yes(a, "TUGSALVAGE") ? "tug salvage" : "",
        yes(a, "PRATIQUE") ? "pratique" : "",
        yes(a, "MED_FACIL") ? "medical" : "",
        yes(a, "GARBAGE") ? "garbage disposal" : "",
        yes(a, "DRTYBALLST") ? "dirty ballast" : "",
        comms.length > 0 ? `comms: ${comms.join("/")}` : "",
      ]) +
      sec("Facilities", [
        yes(a, "CARGOWHARF") ? "cargo wharf" : "",
        yes(a, "CARGO_ANCH") ? "cargo anchorage" : "",
        cranes.length > 0 ? `cranes: ${cranes.join("/")}` : "",
        lifts.length > 0 ? `lifts: ${lifts.join(", ")}` : "",
        yes(a, "LONGSHORE") ? "longshore labor" : "",
        yes(a, "ELECTRICAL") ? "shore power" : "",
        yes(a, "ELECREPAIR") ? "electrical repair" : "",
        yes(a, "NAV_EQUIP") ? "nav-equipment service" : "",
        code(a, "REPAIRCODE", REPAIRS) !== "" ? `repairs: ${code(a, "REPAIRCODE", REPAIRS)}` : "",
        yes(a, "DRYDOCK") ? "drydock" : "",
        code(a, "RAILWAY", RAILWAY_SIZE) !== "" ? `marine railway: ${code(a, "RAILWAY", RAILWAY_SIZE)}` : "",
      ]) +
      sec("Supplies", [
        yes(a, "PROVISIONS") ? "provisions" : "",
        yes(a, "WATER") ? "water" : "",
        yes(a, "FUEL_OIL") ? "fuel oil" : "",
        yes(a, "DIESEL") ? "diesel" : "",
        yes(a, "DECKSUPPLY") ? "deck supplies" : "",
        yes(a, "ENG_SUPPLY") ? "engine supplies" : "",
      ]) +
      `<div class="quiet" style="margin-top:6px">World Port Index reference — not for navigation. ` +
        `${DEPTH_BAND_NOTE} ${NO_VERDICT_NOTE}</div>`
    );
  } catch (err) {
    console.warn("Port record unavailable:", err);
    return null;
  }
}

export function createLayerControl(
  map: MlMap,
  opts?: {
    onRange?: (on: boolean) => void;
    /** Render into this container instead of floating on the map. The console
     *  passes the Observations workspace, so layers live in exactly one place. */
    mount?: HTMLElement;
  },
): HTMLElement {
  const el = document.createElement("details");
  el.className = opts?.mount !== undefined ? "layerctl layerctl-hosted" : "layerctl";
  el.open = true; // start expanded — the overlays matter at a glance
  el.innerHTML =
    `<summary>Overlays</summary><div class="lc-body">` +
    OVERLAYS.map(
      (o) =>
        `<label><input type="checkbox" data-ov="${o.id}"${o.defaultOn === true ? " checked" : ""}>` +
        `<span class="lc-swatch" style="background:${o.sw}"></span>` +
        `<span>${o.label}<small>${o.sub}</small></span></label>`,
    ).join("") +
    // Operational range is a client feature, not a data overlay: it toggles the
    // vessel range rings (owned by the console) via the onRange callback, and its
    // per-ring legend is filled in by setVesselRange.
    `<label class="lc-range"><input type="checkbox" data-range checked>` +
    `<span class="lc-swatch lc-swatch-range"></span>` +
    `<span>Operational range<small>1 / 2 / 3-day steaming rings</small></span></label>` +
    `<div class="lc-range-legend"></div>` +
    `</div>`;
  // Don't let a click inside the control fall through to the map (marker move / pick).
  el.addEventListener("mousedown", (e) => e.stopPropagation());
  el.addEventListener("change", (e) => {
    const t = e.target;
    if (t instanceof HTMLInputElement && "range" in t.dataset) {
      opts?.onRange?.(t.checked);
      return;
    }
    void onOverlayToggle(map, e);
  });
  (opts?.mount ?? map.getContainer()).appendChild(el);

  // Inline ⓘ on the most-consulted / derived layers (the jurisdiction sub-layers
  // are covered on the Methods page). The button preventDefaults its click, so it
  // never toggles the checkbox it sits beside.
  const ovHelp: Record<string, HelpKey> = {
    eez: "map.eez",
    ports: "map.ports",
    buoys: "map.buoys",
    fleet: "fleet.ais",
  };
  for (const [id, key] of Object.entries(ovHelp)) {
    const box = el.querySelector<HTMLInputElement>(`input[data-ov="${id}"]`);
    const span = box?.parentElement?.querySelector("span:last-of-type");
    if (span != null) span.appendChild(infoButton(key));
  }
  const rangeSpan = el.querySelector(".lc-range span:last-of-type");
  if (rangeSpan != null) rangeSpan.appendChild(infoButton("map.rings"));

  // Activate the overlays that ship on by default: a checked box only reflects
  // intent, so build and show each default layer now. Failures are independent —
  // one overlay that can't load just unchecks its own box.
  for (const o of OVERLAYS) {
    if (o.defaultOn !== true) continue;
    const box = el.querySelector<HTMLInputElement>(`input[data-ov="${o.id}"]`);
    void applyOverlay(map, o, true, box);
  }
  return el;
}

async function onOverlayToggle(map: MlMap, e: Event): Promise<void> {
  const box = e.target;
  if (!(box instanceof HTMLInputElement) || !box.dataset["ov"]) return;
  const o = OVERLAYS.find((x) => x.id === box.dataset["ov"]);
  if (!o) return;
  await applyOverlay(map, o, box.checked, box);
}

// applyOverlay builds an overlay's layers (once) and sets their visibility. It
// backs both the startup activation of default layers and the checkbox toggles;
// box (when given) is disabled during the async build and unchecked on failure.
async function applyOverlay(
  map: MlMap,
  o: OverlayDef,
  visible: boolean,
  box: HTMLInputElement | null,
): Promise<void> {
  if (box) box.disabled = true;
  try {
    const ids = await o.ensure(map);
    const vis = visible ? "visible" : "none";
    for (const l of ids) {
      if (map.getLayer(l)) map.setLayoutProperty(l, "visibility", vis);
    }
  } catch (err) {
    console.warn("overlay", o.id, "unavailable:", err);
    if (box) box.checked = false;
  }
  if (box) box.disabled = false;
}

// ===================================================================================
//  EEZ point-identify — which maritime jurisdiction a click sits in, and whether it
//  is a contested/shared zone (the operationally decisive fact for permitting).
// ===================================================================================
export async function fetchJurisdiction(
  lat: number,
  lon: number,
): Promise<Jurisdiction | null> {
  try {
    const d = await getJSON(
      `${MR}/ows?service=WFS&version=1.0.0&request=GetFeature` +
        `&typeName=MarineRegions:eez&maxFeatures=1&outputFormat=application/json` +
        `&propertyName=geoname,territory1,sovereign1,pol_type` +
        `&cql_filter=${encodeURIComponent(`INTERSECTS(the_geom,POINT(${lon} ${lat}))`)}`,
    );
    const fc = asFeatureCollection(d);
    const props = fc?.features?.[0]?.properties;
    if (!props) {
      // Empty result = high seas, beyond any national EEZ. This is a successful
      // answer, distinct from a fetch failure (which returns null below).
      return {
        name: "High seas",
        territory: null,
        sovereign: null,
        disputed: false,
        polType: "none",
      };
    }
    const p = props as Record<string, unknown>;
    const polType = str(p, "pol_type");
    return {
      name: str(p, "geoname") || "Unknown",
      territory: str(p, "territory1") || null,
      sovereign: str(p, "sovereign1") || null,
      // v1: any pol_type other than the plain 200 nm EEZ marks a contested/shared zone.
      disputed: polType !== "" && polType !== "200NM",
      polType: polType || "none",
    };
  } catch (err) {
    console.warn("Jurisdiction lookup unavailable:", err);
    return null;
  }
}
